import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LibraryMusicIcon from '@mui/icons-material/LibraryMusic';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Timeline, TimelineState } from '@xzdarcy/react-timeline-editor';
import { TimelineRow, TimelineAction, TimelineEffect } from '@xzdarcy/timeline-engine';
import '@xzdarcy/react-timeline-editor/dist/react-timeline-editor.css';

import SettingsService from 'services/SettingsService';
import VideoEditingService, {
  VideoClip, SourceContext, EditPlan, EditPlanSummary, EditorTimeline, TLVideoClip, TLOverlayClip, TLAudioClip, emptyTimeline,
  ClipSegment, ClipTranscript, ClipLabelVocabulary, BrollAsset, PreviewStatus, EDIT_PLAN_STATUS,
} from 'services/VideoEditingService';
import { StoryboardSegment } from 'pages/VideoCreative/types';
import { brollSegmentId, isBrollSegmentId } from '../brollAssets';
import {
  BRIEF_DEFAULTS, BRIEF_DEFAULTS_NAMESPACE, BriefDefaultKey, DEFAULT_BRIEF_CHOICE,
  hookOverlayFor, isUntouchedHookOverlay, mergeBriefDefaults, missingBriefRequirements,
} from '../briefTemplates';
import BriefDefaultsDialog from './BriefDefaultsDialog';
import TimelinePreview, { TimelinePreviewHandle } from './TimelinePreview';
import RangeTrimmer from './RangeTrimmer';
import ScrubberSettingsButton from './ScrubberSettingsButton';
import TimelineZoomControls, {
  captionFontForTimelineZoom, clampTimelineZoom, DEFAULT_MAIN_TIMELINE_ZOOM,
} from './TimelineZoomControls';
import HookIdeasDialog from './HookIdeasDialog';
import { HOOK_PROMPT_SETTINGS_NAMESPACE, HOOK_PROMPT_SETTINGS_KEY, HEADLINE_PROMPT_SETTINGS_KEY } from '../hookSettings';
import ProjectCopyPanel from './ProjectCopyPanel';
import CachedRangePreviewFrame from './CachedRangePreviewFrame';
import { AUDIO_DRAG_MIME, CLIP_DRAG_MIME, SEGMENT_DRAG_MIME } from './ClipBrowser';
import type { LinkedTimelineClip, NewClipSegment } from './ClipBrowser';
import { useEditHistory } from '../useEditHistory';
import { generateInstructionsPdf } from '../generateInstructionsPdf';
import { formatDuration } from '../format';
import {
  hasCaptionsWorthShowing, timelineCaptionSpans,
} from '../captionGrouping';
import { useVideoEditingFontScale } from '../fontScale';
import useActorName from 'shared/hooks/useActorName';
import {
  clipDur, videoLayout, timelineDuration, sourceLength, audioCoversSpan,
  videoClipFromSource, audioClipFromSource, newAudioTrack, audioTrackEnd, removeClipById,
  addSegmentClipToTimeline, removeSegmentClips, removeSourceClips, timelineClipUsage,
  timelineClipStart, splitTimelineClipWithSegments,
  toggleTimelineClipSelection, uid,
  overlayClips, overlayLayout, overlayClipFromSource, resolveOverlayOverlaps, overlayNeighbourBounds,
} from '../timelineModel';

const service = new VideoEditingService();
const settingsService = new SettingsService();

export interface TimelineEditorHandle {
  // Append clips to the timeline (video → video track, audio → last audio track),
  // auto-creating a plan if none is open yet. Called by the clip browser.
  addClips: (clips: VideoClip[]) => Promise<void>;
  // Append a saved segment of a source clip as a pre-trimmed timeline clip.
  addSegmentClip: (source: VideoClip, segment: ClipSegment) => Promise<boolean>;
  // Create/delete plan-owned saved clips. These never mutate another plan.
  saveSegment: (segment: NewClipSegment) => Promise<ClipSegment | null>;
  deleteSegment: (segmentId: string) => boolean;
  // Remove every split piece belonging to one original or saved clip.
  removeSourceClip: (sourceFileId: string) => boolean;
  removeSegmentClip: (segmentId: string) => boolean;
  // Keep the source Clips tab linked to a concrete timeline instance.
  selectClipForSource: (sourceFileId: string) => void;
  selectClipForSegment: (segmentId: string) => void;
  trimSourceClip: (sourceFileId: string, inSec: number, outSec: number) => boolean;
  trimSegmentClip: (segmentId: string, inSec: number, outSec: number) => boolean;
  // Remove a source from both this project's Clips tab and its timeline in one
  // undoable edit. The Drive source itself is never deleted.
  removeClipFromPlan: (sourceFileId: string) => boolean;
  // Add a source to this project's Clips tab without touching the timeline
  // (e.g. a freshly extracted audio file).
  addSourceToPlan: (sourceFileId: string) => boolean;
  // Add a source clip as a B-roll cutaway aligned to the start of one main-track
  // clip (picture replaces the main track there; the main clip's audio continues).
  addOverlayClip: (clip: VideoClip, targetVideoClipId?: string | null) => boolean;
  // Pull a library clip into the plan (asset record + its draggable segment), or drop one along
  // with any placements it already has on a track.
  addBrollAsset: (asset: BrollAsset, segment: ClipSegment) => void;
  removeBrollAsset: (labelId: string) => void;
  // Pause the composed preview. The page calls this when the clip editor modal
  // opens so timeline audio doesn't keep playing underneath.
  pausePreview: () => void;
  // Deselect any selected timeline clip — called when the clip editor closes.
  clearSelection: () => void;
  // Set the editor note on the currently-selected timeline clip (from the
  // clip-editor modal's note panel).
  setSelectedClipNote: (note: string) => void;
}

interface Props {
  clips: VideoClip[];
  // Source files the timeline must be able to resolve but the project does NOT own — today,
  // the files behind library b-roll. They join `clipsById` only: `clips` is what plan
  // membership is computed from, and a foreign creator's file must never end up in that.
  extraClips?: VideoClip[];
  source: SourceContext;
  segments: StoryboardSegment[] | null;
  // Reports the source fileIds currently used anywhere on the timeline, so the clip
  // browser can flag which clips are already in the edit.
  onUsedClipsChange?: (fileIds: string[], segmentIds: string[]) => void;
  onSelectedClipChange?: (clip: LinkedTimelineClip | null) => void;
  // A double-click on a timeline clip requests opening the clip editor modal
  // (single click only selects).
  onRequestClipEdit?: () => void;
  // Keeps the Clips tab on the same saved plan as the timeline editor.
  onPlanClipsChange?: (sourceFileIds: string[]) => void;
  onPlanSegmentsChange?: (segments: ClipSegment[]) => void;
  // Library b-roll pulled into this plan, pushed up so the page can re-resolve its signed URLs.
  onPlanBrollAssetsChange?: (assets: BrollAsset[]) => void;
  // The open plan's product, so the page can pre-filter the b-roll picker to it.
  onPlanProductCategoryChange?: (productCategory: string | null) => void;
  // Read-only compatibility source for plans saved before clips became plan-owned.
  legacyClipSegments?: ClipSegment[];
  // The clip-label vocabulary the page loads once; supplies the project's product
  // type dropdown. Null until it has loaded.
  clipLabelVocabulary?: ClipLabelVocabulary | null;
  // The page owns the toolbar row; plan controls render into this slot.
  planControlsContainer?: HTMLElement | null;
  // The page owns the shared stage (big player area); the composed preview /
  // selected-clip editor render into this slot. Absent → rendered inline.
  stageContainer?: HTMLElement | null;
  // When set (the clip editor modal is open), the whole timeline dock renders
  // here instead of in the page dock, so all tracks are editable in the modal.
  timelineContainer?: HTMLElement | null;
  // When a load requests a specific saved project (opened from the global "Saved
  // projects" picker), open THIS plan instead of defaulting to the newest.
  initialPlanId?: string | null;
  // Bumped by the page on every load. Included in the plan-loading effect's deps so
  // it re-fires even when the picked project's folder is already loaded (the folder
  // id alone wouldn't change, so the effect otherwise wouldn't run).
  loadNonce?: number;
  // Per-source transcode status, already polled by the page for the clip rail. The
  // timeline uses it to say why a clip's bar is still empty.
  previewStatuses?: Record<string, PreviewStatus>;
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

const TIMELINE_ELEMENT_HEIGHT_SCALE = 1.2;
// Video/B-roll rows run 15% taller than the audio base so the label bar (title +
// time) has room without crowding the filmstrip underneath it.
const VIDEO_ROW_HEIGHT_SCALE = 1.15;
const ROW_HEIGHT = Math.round(46 * VIDEO_ROW_HEIGHT_SCALE * TIMELINE_ELEMENT_HEIGHT_SCALE);
// The audio and video-caption lanes carry transcript strips; their heights are
// computed at render time from the strip height (see videoCaptionRowHeight /
// audioRowHeight) so the row hugs its content instead of leaving dead space.
const RULER_HEIGHT = 32;
const TIMELINE_SCROLLBAR_HEIGHT = 14;
const TIMELINE_SCROLLBAR_GAP = 8;
const TIMELINE_START_LEFT = 20;
// Effect ids are used only to tag which lane an action belongs to (we drive
// playback ourselves in TimelinePreview, so the effects have no run source).
const EFFECTS: Record<string, TimelineEffect> = {
  video: { id: 'video' },
  overlay: { id: 'overlay' },
  audio: { id: 'audio' },
  videocaptions: { id: 'videocaptions' },
  script: { id: 'script' },
  ghost: { id: 'ghost' },
};

const planPayload = (p: EditPlan) => ({
  name: p.name, status: p.status, notes: p.notes, script: p.script ?? null,
  hook: p.hook ?? null, headline: p.headline ?? null, cta: p.cta ?? null,
  productCategory: p.productCategory ?? null,
  hookOverlay: p.hookOverlay ?? null,
  editingDirection: p.editingDirection ?? null,
  finalCta: p.finalCta ?? null,
  subtitlesStyle: p.subtitlesStyle ?? null,
  musicStyle: p.musicStyle ?? null,
  timeline: p.timeline,
});

// What the "you still owe this" warning says, for the three states it can be in.
function briefGapSentence(gaps: { hook: boolean; cta: boolean } | null): string {
  if (!gaps) return '';
  if (gaps.hook && gaps.cta) return 'Add a hook and the CTA copy';
  return gaps.hook ? 'Add a hook' : 'Add the CTA copy';
}

// The clip's title + time on a solid bar at the top of the timeline clip,
// separate from (and above) the filmstrip frames, so it stays legible.
function ClipLabelBar({ name, meta, icon, tag, fontScale, background = 'rgba(15,23,42,0.9)' }: {
  name: string; meta: string; icon?: React.ReactNode; tag?: string; fontScale: number; background?: string;
}) {
  return (
    <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 0.5, px: 0.6, py: '2px', backgroundColor: background }}>
      {icon}
      {tag && (
        <Typography sx={{ fontSize: 8 * fontScale, fontWeight: 800, letterSpacing: '0.04em', color: '#fff', backgroundColor: 'rgba(124,58,237,0.9)', px: 0.5, borderRadius: '3px', flexShrink: 0 }}>{tag}</Typography>
      )}
      <Typography sx={{ fontSize: 10.5 * fontScale, fontWeight: 600, color: '#fff', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</Typography>
      {meta && <Typography sx={{ ml: 'auto', pr: 2, fontSize: 9.5 * fontScale, color: 'rgba(255,255,255,0.8)', flexShrink: 0, whiteSpace: 'nowrap' }}>{meta}</Typography>}
    </Box>
  );
}

// Why a clip's bar is empty: its preview is still being prepared, or it failed.
//
// A clip only becomes playable and only grows a filmstrip once its transcode is DONE —
// until then the bar is a blank rectangle with no explanation, which reads as a broken
// clip rather than one that isn't ready. Deliberately quiet: it sits over the filmstrip
// area, not the label, and on a narrow bar it drops to just the spinner.
//
// A FAILED clip gets a static marker instead of a spinner. A spinner that never stops is
// worse than no spinner — it promises progress that is not coming.
// An UNKNOWN status says nothing either. previewStatuses covers only the video clips in
// the current folder listing and is empty until the first poll lands, so treating a
// missing entry as "pending" badged every clip "Preparing…" for the first seconds of
// every project — and permanently for a clip whose source has left the folder. That is
// the same "looks broken" reading this badge exists to prevent.
function ClipMediaBadge({ status, pxWidth, fontScale }: {
  status: PreviewStatus | undefined; pxWidth: number; fontScale: number;
}) {
  if (!status || status === 'ready') return null;
  const failed = status === 'error';
  const roomForText = pxWidth > 96;
  return (
    <Box
      sx={{
        position: 'absolute', inset: 0, zIndex: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.6,
        pointerEvents: 'none', color: failed ? '#fca5a5' : 'rgba(255,255,255,0.75)',
      }}
    >
      {failed
        ? <ErrorOutlineIcon sx={{ fontSize: 13 * fontScale }} />
        : <CircularProgress size={12 * fontScale} thickness={5} sx={{ color: 'rgba(255,255,255,0.65)' }} />}
      {roomForText && (
        <Typography sx={{ fontSize: 9.5 * fontScale, fontWeight: 600, letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>
          {failed ? 'Preview failed' : 'Preparing…'}
        </Typography>
      )}
    </Box>
  );
}

// A thumbnail filmstrip drawn as the background of a video/B-roll timeline clip:
// evenly-spaced frames across the clip's TRIMMED source range so the bar shows
// its own footage. Cell count follows the bar's on-screen width so it stays a
// filmstrip at any zoom (capped so a long clip doesn't spawn dozens of captures).
function ClipFilmstrip({ source, inSec, outSec, pxWidth, loadEnabled }: {
  source: VideoClip;
  inSec: number;
  outSec: number;
  pxWidth: number;
  loadEnabled: boolean;
}) {
  const frames = useMemo(() => {
    const dur = Math.max(0.1, outSec - inSec);
    const aspect = source.width && source.height ? source.width / source.height : 9 / 16;
    const cellWidth = Math.max(18, ROW_HEIGHT * aspect);
    const count = Math.max(1, Math.min(16, Math.round(pxWidth / cellWidth)));
    return Array.from({ length: count }, (_, i) => {
      const sourceTimeSec = inSec + ((i + 0.5) / count) * dur;
      return {
        id: `${source.fileId}:${sourceTimeSec.toFixed(2)}`,
        src: source.streamUrl,
        sourceTimeSec,
        sourceId: source.fileId,
        sourceDurationSec: source.durationSec ?? undefined,
        width: source.width,
        height: source.height,
      };
    });
  }, [source, inSec, outSec, pxWidth]);
  return (
    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', overflow: 'hidden', borderRadius: '3px', pointerEvents: 'none' }}>
      {frames.map((frame) => (
        <Box key={frame.id} sx={{ flex: '1 1 0', minWidth: 0, height: '100%' }}>
          <CachedRangePreviewFrame frame={frame} loadEnabled={loadEnabled} />
        </Box>
      ))}
      {/* Darkening scrim so the white clip label/duration stay legible over frames. */}
      <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(15,23,42,0.55) 0%, rgba(15,23,42,0.2) 45%, rgba(15,23,42,0.55) 100%)' }} />
    </Box>
  );
}

// Older plans predate per-plan clip membership. Treat their full folder as the
// saved default, while preserving an explicit empty set on newer plans.
const planSourceClipIds = (timeline: EditorTimeline, clips: VideoClip[]) => Array.from(new Set(
  timeline.sourceClipIds == null ? clips.map((clip) => clip.fileId) : timeline.sourceClipIds,
));

// Folder-level segments are a read-only bridge for plans saved before segment
// ownership moved into the plan document. Any mutation materializes this list in
// the active plan, after which it is fully independent from every other plan.
const planClipSegments = (timeline: EditorTimeline, legacyClipSegments: ClipSegment[]) => (
  timeline.clipSegments == null ? legacyClipSegments : timeline.clipSegments
);

// Placing a segment normally records its file as one of the plan's own clips. Library b-roll is
// borrowed — the file lives in another creator's folder — so it stays out of that list, or the
// Clips tab and removeClipFromPlan would treat someone else's footage as this project's.
const sourceIdsAfterPlacing = (ids: string[], segment: ClipSegment, fileId: string) => (
  isBrollSegmentId(segment.segmentId) ? ids : Array.from(new Set([...ids, fileId]))
);

/**
 * Which lane a drop at this height belongs to: whichever is NEARER, not "inside the B-roll row
 * or else the main track".
 *
 * The ruler sits directly above the B-roll row, so a clip released over it used to land on the
 * main track — spliced into the edit, pushing everything after it — when the lane a few pixels
 * away was plainly the target. Ties go to B-roll: it is the additive, harmless one.
 *
 * Pure and exported so the rule can be tested; the DOM rects come from the caller.
 */
export function nearestLane(
  clientY: number, overlay: { top: number; bottom: number }, video: { top: number; bottom: number } | null,
): 'video' | 'overlay' {
  const distance = (rect: { top: number; bottom: number }) => {
    if (clientY >= rect.top && clientY <= rect.bottom) return 0;
    return clientY < rect.top ? rect.top - clientY : clientY - rect.bottom;
  };
  if (!video) return distance(overlay) === 0 ? 'overlay' : 'video';
  return distance(overlay) <= distance(video) ? 'overlay' : 'video';
}

// Put a library clip on the B-roll (cutaway) lane, after whatever is already there so repeated
// adds don't pile up at the same instant. Returns the timeline unchanged if the clip is already
// placed, which is how the caller tells "added" from "was already there".
function addBrollToOverlayTrack(
  base: EditorTimeline, source: VideoClip, segment: ClipSegment,
): EditorTimeline {
  const placed = overlayClips(base);
  if (timelineClipUsage(base).segmentIds.includes(segment.segmentId)) return base;
  const startAt = placed.reduce(
    (end, clip) => Math.max(end, clip.timelineStartSec + Math.max(0, clip.sourceOutSec - clip.sourceInSec)),
    0,
  );
  const clip = {
    ...overlayClipFromSource(source, startAt),
    segmentId: segment.segmentId,
    fileName: segment.name?.trim() || segment.fileName || source.name,
    sourceInSec: segment.inSec,
    sourceOutSec: segment.outSec,
  };
  return { ...base, overlayTrack: { clips: [...placed, clip] } };
}

// localStorage namespace for per-plan undo/redo history (persists across reloads).
const HISTORY_KEY_PREFIX = 'sbly-tools:video-edit-history';

// Mac uses ⌘; Windows/Linux use Ctrl. Drives both the shortcut hints and (for
// completeness) reads naturally in the tooltips.
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || '');
const UNDO_HINT = IS_MAC ? '⌘Z' : 'Ctrl+Z';
const REDO_HINT = IS_MAC ? '⌘⇧Z' : 'Ctrl+Y';

// Fallback per-segment length for laying out the script reference lane.
const scriptSegLen = (s: StoryboardSegment) => (s.durationSec && s.durationSec > 0 ? s.durationSec : 3);

// Probe a media URL for its real duration (seconds) via a throwaway media element.
// Used for audio clips, whose duration Drive doesn't report. Resolves null on
// error/timeout so the caller can fall back to a default length.
function probeDurationSec(url: string): Promise<number | null> {
  return new Promise((resolve) => {
    const el = document.createElement('audio');
    el.preload = 'metadata';
    let settled = false;
    const done = (v: number | null) => {
      if (settled) return;
      settled = true;
      el.removeAttribute('src');
      try { el.load(); } catch { /* ignore */ }
      resolve(v);
    };
    el.onloadedmetadata = () => done(isFinite(el.duration) && el.duration > 0 ? el.duration : null);
    el.onerror = () => done(null);
    setTimeout(() => done(null), 8000);
    el.src = url;
  });
}

// Stable default, so an absent `extraClips` doesn't rebuild the clip map every render.
const NO_EXTRA_CLIPS: VideoClip[] = [];

const TimelineEditor = forwardRef<TimelineEditorHandle, Props>(function TimelineEditor(
  {
    clips, extraClips = NO_EXTRA_CLIPS, source, segments, onUsedClipsChange, onSelectedClipChange, onRequestClipEdit,
    onPlanClipsChange, onPlanSegmentsChange, onPlanBrollAssetsChange, onPlanProductCategoryChange, legacyClipSegments = [], planControlsContainer, stageContainer, timelineContainer,
    initialPlanId, loadNonce, clipLabelVocabulary = null, previewStatuses = {},
  }, ref,
) {
  const fontScale = useVideoEditingFontScale();
  // Current dashboard user (top-bar selector) — stamped as a new project's creator.
  // Creating requires one so no project is ever saved unattributed.
  const [actor] = useActorName();
  const [needUserToCreate, setNeedUserToCreate] = useState(false);
  // Populated so create/delete refresh the top-of-page picker's list; the value
  // is no longer shown here (the per-source dropdown was removed).
  const [, setPlans] = useState<EditPlanSummary[]>([]);
  const [plan, setPlan] = useState<EditPlan | null>(null);
  const [busy, setBusy] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [instrLoading, setInstrLoading] = useState(false);
  const [instrError, setInstrError] = useState<string | null>(null);
  const [instructionsDialogOpen, setInstructionsDialogOpen] = useState(false);
  const [hookDialogOpen, setHookDialogOpen] = useState(false);
  const [briefDefaultsOpen, setBriefDefaultsOpen] = useState(false);
  // The shared wording behind the brief's [DEFAULT …] tokens, so the panel can show the
  // sentences rather than the tokens. Null until the settings request lands — the panel
  // reads that as "not loaded yet" and keeps its editing affordances disabled.
  const [briefDefaults, setBriefDefaults] = useState<Record<BriefDefaultKey, string> | null>(null);
  const [splitting, setSplitting] = useState(false);
  const [splitError, setSplitError] = useState<string | null>(null);
  // Which required brief fields were empty the last time Generate was pressed. Drives
  // both the warning and the red outline on the offending boxes; cleared as soon as the
  // user starts filling one in, so the page stops nagging the moment they act on it.
  const [briefGaps, setBriefGaps] = useState<{ hook: boolean; cta: boolean } | null>(null);
  const [copyOk, setCopyOk] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [readyPreviewKey, setReadyPreviewKey] = useState<string | null>(null);
  // Once the composed preview has been ready once, keep filmstrips capturing —
  // otherwise adding a clip (which changes the preview key) blanks every clip's
  // frames until the preview re-fires canplay, which it may never do.
  const [framesReadyOnce, setFramesReadyOnce] = useState(false);
  const [displayTime, setDisplayTime] = useState(0);
  const [zoom, setZoom] = useState(DEFAULT_MAIN_TIMELINE_ZOOM); // px per second (scaleWidth, since scale=1s)
  const [previewIn, setPreviewIn] = useState(0);
  const [hidePreviewBackgrounds, setHidePreviewBackgrounds] = useState(false);
  // Null means "follow the full timeline length" as clips are added or removed.
  const [previewOut, setPreviewOut] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // The script-reference lane (hook/solution/… beats) is hidden by default to
  // reduce clutter; the editor can reveal it when mapping cuts to the script.
  const [showScriptLane, setShowScriptLane] = useState(false);
  // Which track a dragged library clip would drop into (highlights that lane),
  // and the cursor x within the timeline (draws the drop-position indicator).
  const [dropTarget, setDropTarget] = useState<'video' | 'overlay' | 'audio' | null>(null);
  const [dropX, setDropX] = useState<number | null>(null);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [deletePlanDialogOpen, setDeletePlanDialogOpen] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');

  const skipSave = useRef(true);
  const timelineStateRef = useRef<TimelineState | null>(null);
  // Scroll position a ctrl+wheel zoom wants restored once the timeline repaints
  // (see the zoom-scroll reconcile effect).
  const pendingScrollAnchorRef = useRef<number | null>(null);
  const previewRef = useRef<TimelinePreviewHandle | null>(null);
  const timelineViewportRef = useRef<HTMLDivElement | null>(null);
  const playheadRef = useRef(0);
  const lastDisplay = useRef(0);
  // Tracks the last clicked action + time to detect a double click (open editor).
  const lastActionClick = useRef<{ id: string; t: number } | null>(null);
  const pendingSourceAdds = useRef(new Set<string>());
  const pendingSegmentAdds = useRef(new Set<string>());

  // Save tracking so no progress is lost. `editSeq` bumps on every change; `savedSeq`
  // records the last change actually persisted. They differ ⇢ there are unsaved edits.
  const editSeq = useRef(0);
  const savedSeq = useRef(0);
  const latestPlanRef = useRef<EditPlan | null>(null);
  latestPlanRef.current = plan;
  const isDirty = () => editSeq.current !== savedSeq.current;

  // Everything the timeline can resolve for playback, thumbnails and trimming — the project's
  // own clips plus the borrowed ones. Deliberately the ONLY place `extraClips` is read; every
  // other use of `clips` below means "the clips this plan owns".
  const clipsById = useMemo(() => {
    const m = new Map<string, VideoClip>();
    for (const c of clips) m.set(c.fileId, c);
    for (const c of extraClips) if (!m.has(c.fileId)) m.set(c.fileId, c);
    return m;
  }, [clips, extraClips]);

  const timeline: EditorTimeline = plan?.timeline || emptyTimeline();
  const sourceClipIds = planSourceClipIds(timeline, clips);
  const sourceClipIdsKey = JSON.stringify(sourceClipIds);
  useEffect(() => {
    onPlanClipsChange?.(sourceClipIds);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [plan?.planId, sourceClipIdsKey, onPlanClipsChange]);
  const ownedClipSegments = plan ? planClipSegments(timeline, legacyClipSegments) : [];
  const ownedClipSegmentsKey = JSON.stringify(ownedClipSegments);
  useEffect(() => {
    onPlanSegmentsChange?.(ownedClipSegments);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [plan?.planId, ownedClipSegmentsKey, onPlanSegmentsChange]);
  const ownedBrollAssets = plan ? (timeline.brollAssets || []) : [];
  const ownedBrollAssetsKey = JSON.stringify(ownedBrollAssets);
  useEffect(() => {
    onPlanBrollAssetsChange?.(ownedBrollAssets);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [plan?.planId, ownedBrollAssetsKey, onPlanBrollAssetsChange]);
  useEffect(() => {
    onPlanProductCategoryChange?.(plan?.productCategory ?? null);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [plan?.planId, plan?.productCategory, onPlanProductCategoryChange]);
  const layout = useMemo(() => videoLayout(timeline), [timeline]);
  const overlayLaid = useMemo(() => overlayLayout(timeline), [timeline]);
  const totalDuration = useMemo(() => timelineDuration(timeline), [timeline]);
  const previewStart = Math.min(previewIn, Math.max(0, totalDuration - 0.2));
  const previewEnd = Math.max(previewStart, Math.min(previewOut ?? totalDuration, totalDuration));
  const captionFontSize = captionFontForTimelineZoom(zoom) * fontScale;
  // Room for up to 5 caption lines (see WebkitLineClamp below) so longer
  // sentence-level captions aren't cut off early.
  const captionStripHeight = Math.min(112 * fontScale, Math.max(72 * fontScale, captionFontSize * 6.2));
  // Transcript-carrying lanes are sized to their strip (plus the audio lane's
  // label bar) so the row is never taller than its content — otherwise dead
  // dark space shows beneath the captions.
  const CAPTION_LANE_PADDING = 8;
  const clipLabelBarHeight = Math.round(16 * fontScale + 4);
  const videoCaptionRowHeight = Math.round(captionStripHeight + CAPTION_LANE_PADDING);
  const audioRowHeight = Math.round(clipLabelBarHeight + captionStripHeight + CAPTION_LANE_PADDING);
  const playbackRange = useMemo(
    () => ({ startSec: previewStart, endSec: previewEnd }),
    [previewStart, previewEnd],
  );

  // A newly opened plan starts with its whole timeline selected for playback.
  useEffect(() => {
    setPreviewIn(0);
    setPreviewOut(null);
  }, [plan?.planId]);

  // Zoom the timeline so the whole thing fits the width it actually has — no horizontal
  // scroll to see the end of the edit.
  //
  // Read through a ref so the callback itself is stable: the effect below must fire when
  // the timeline MOVES, not every time an edit changes the duration, which would fight
  // someone who has zoomed in to work on a detail.
  const totalDurationRef = useRef(totalDuration);
  totalDurationRef.current = totalDuration;
  const fitZoomToViewport = useCallback(() => {
    const duration = totalDurationRef.current;
    const usable = (timelineViewportRef.current?.clientWidth ?? 0) - TIMELINE_START_LEFT * 2;
    if (usable <= 0 || duration <= 0) return false;
    // Rounded DOWN, not to nearest. `clampTimelineZoom` rounds, and px-per-second is
    // multiplied by the whole duration — so rounding up by half a pixel puts half a pixel
    // times the length of the edit past the right edge, which on a minute-long timeline is
    // more than the slack the start offset leaves. Undershooting costs a hair of width;
    // overshooting costs a horizontal scrollbar on a timeline that was meant to fit.
    setZoom(clampTimelineZoom(Math.floor(usable / duration)));
    return true;
  }, []);

  // Re-taken when a different plan opens AND when the dock is portaled into the clip
  // editor modal or back out of it. That portal changes the width the timeline has — the
  // modal is a fraction of the page — so a zoom fitted to the page left the modal
  // scrolling sideways to see the end of the edit.
  //
  // Clamped to the normal zoom bounds, so a very long project still bottoms out at the
  // minimum and scrolls rather than shrinking to something unreadable.
  useEffect(() => {
    if (!plan?.planId) return undefined;
    // The modal animates in, so the frame right after the portal mounts can still measure
    // zero. Watch for the real size rather than guessing at a delay.
    if (fitZoomToViewport() || typeof ResizeObserver === 'undefined') return undefined;
    const viewport = timelineViewportRef.current;
    if (!viewport) return undefined;
    const observer = new ResizeObserver(() => { if (fitZoomToViewport()) observer.disconnect(); });
    observer.observe(viewport);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan?.planId, timelineContainer, fitZoomToViewport]);

  // Command/Ctrl + wheel zooms around the point under the mouse. A native,
  // non-passive listener is required so the browser's page zoom does not win.
  useEffect(() => {
    const root = timelineViewportRef.current;
    if (!root) return undefined;
    const handleWheel = (event: WheelEvent) => {
      if (!event.metaKey && !event.ctrlKey) return;
      event.preventDefault();
      const canvas = root.querySelector('.timeline-editor') as HTMLElement | null;
      const scrollGrid = root.querySelector('.timeline-editor-edit-area .ReactVirtualized__Grid') as HTMLElement | null;
      const rect = canvas?.getBoundingClientRect();
      const pointerX = rect ? event.clientX - rect.left : 0;
      const scrollLeft = scrollGrid?.scrollLeft || 0;
      const wheelFactor = Math.min(1.35, Math.max(0.74, Math.exp(-event.deltaY * 0.0025)));
      setZoom((current) => {
        const next = clampTimelineZoom(current * wheelFactor);
        if (next === current) return current;
        const timeAtPointer = Math.max(0, (scrollLeft + pointerX - TIMELINE_START_LEFT) / current);
        // Keep the time under the pointer fixed so close-up navigation feels
        // anchored. The zoom effect below applies this once the timeline has
        // repainted, then syncs the library to whatever the browser settled on.
        pendingScrollAnchorRef.current = Math.max(0, TIMELINE_START_LEFT + timeAtPointer * next - pointerX);
        return next;
      });
    };
    root.addEventListener('wheel', handleWheel, { passive: false });
    return () => root.removeEventListener('wheel', handleWheel);
  }, [plan?.planId]);

  // Keep the timeline library's scroll state in step with the real scroll box after
  // a zoom change.
  //
  // The library draws the playhead at `timeToPx(time) - scrollLeft`, using a
  // scrollLeft it holds in its OWN state and only updates from scroll EVENTS. The
  // rows and ruler live inside the scroll box, so they always re-lay out correctly;
  // the playhead does not. Zooming out shrinks the content, the browser silently
  // clamps the box's scrollLeft to the new (smaller) maximum WITHOUT firing a
  // scroll event, and the library keeps subtracting the old, larger value — so the
  // playhead is drawn well to the left of its true time, by more the further you
  // were scrolled. Click-to-seek reads the same stale value, which is why a click
  // could jump the playhead to a completely different second.
  //
  // Only the ctrl+wheel path above set the scroll deliberately; the zoom slider and
  // fit-to-width left it stale. So every zoom change ends here: apply the pointer
  // anchor if ctrl+wheel asked for one, let the browser clamp it to what is
  // actually reachable, then tell the library that clamped truth. Two frames: one
  // for React to repaint at the new scale, one for the browser to settle the clamp.
  useEffect(() => {
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        const grid = timelineViewportRef.current
          ?.querySelector('.timeline-editor-edit-area .ReactVirtualized__Grid') as HTMLElement | null;
        if (!grid) return;
        const anchor = pendingScrollAnchorRef.current;
        pendingScrollAnchorRef.current = null;
        if (anchor != null) grid.scrollLeft = anchor;
        timelineStateRef.current?.setScrollLeft(grid.scrollLeft);
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [zoom]);

  // Report original clips and named saved sub-clips separately. A saved sub-clip
  // shares its Drive source id, but it is a distinct item in the Clips tab.
  const usedClips = useMemo(() => timelineClipUsage(timeline), [timeline]);
  const usedKey = `${usedClips.sourceFileIds.join(',')}|${usedClips.segmentIds.join(',')}`;
  useEffect(() => {
    onUsedClipsChange?.(usedClips.sourceFileIds, usedClips.segmentIds);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [usedKey]);

  // Audio tracks start transcription automatically. Video sources are status-only
  // until the editor explicitly enables transcription from a selected video.
  const [transcripts, setTranscripts] = useState<Record<string, ClipTranscript>>({});
  const [transcriptPollVersion, setTranscriptPollVersion] = useState(0);
  const [retryingTranscriptIds, setRetryingTranscriptIds] = useState<Set<string>>(() => new Set());
  const [enabledVideoTranscriptIds, setEnabledVideoTranscriptIds] = useState<Set<string>>(() => new Set());
  const audioIdsKey = useMemo(() => {
    const ids = new Set<string>();
    for (const tr of timeline.audioTracks) for (const c of tr.clips) ids.add(c.sourceFileId);
    return Array.from(ids).sort().join(',');
  }, [timeline]);
  const videoIdsKey = useMemo(() => Array.from(new Set(
    timeline.videoTrack.clips.map((clip) => clip.sourceFileId),
  )).sort().join(','), [timeline]);
  const enabledVideoTranscriptIdsKey = useMemo(
    () => Array.from(enabledVideoTranscriptIds).sort().join(','),
    [enabledVideoTranscriptIds],
  );
  const failedTranscripts = useMemo(() => {
    const activeIds = new Set(audioIdsKey ? audioIdsKey.split(',') : []);
    const currentVideoIds = new Set(videoIdsKey ? videoIdsKey.split(',') : []);
    enabledVideoTranscriptIds.forEach((fileId) => {
      if (currentVideoIds.has(fileId)) activeIds.add(fileId);
    });
    return Object.entries(transcripts)
      .filter(([fileId, transcript]) => activeIds.has(fileId) && transcript.status === 'error')
      .map(([fileId, transcript]) => ({
        fileId,
        fileName: clips.find((clip) => clip.fileId === fileId)?.name || fileId,
        error: transcript.error || 'Transcription failed.',
      }));
  }, [audioIdsKey, clips, enabledVideoTranscriptIds, transcripts, videoIdsKey]);
  useEffect(() => {
    const ids = audioIdsKey ? audioIdsKey.split(',') : [];
    if (ids.length === 0) return undefined;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // start=true on EVERY poll, not just the first: audio clips are meant to
    // transcribe automatically, so if the first request's queueing was lost there
    // was nothing to retry it and the clip waited forever. The server only queues
    // clips that have not started, and queueing is deduped by file id, so repeats
    // cost nothing once one sticks.
    async function poll() {
      const resp = await service.getClipTranscripts(ids, true);
      if (stopped) return;
      if (resp) {
        setTranscripts((current) => ({ ...current, ...resp.transcripts }));
        autoTrimSilence(resp.transcripts);
      }
      const done = resp && ids.every((id) => {
        const st = resp.transcripts[id]?.status;
        return st === 'ready' || st === 'error';
      });
      if (!done) timer = setTimeout(poll, 3000);
    }
    poll();
    return () => { stopped = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioIdsKey, transcriptPollVersion]);

  // Discover already-finished video transcripts without starting new Whisper
  // work. Once a video is enabled, keep polling its pending/transcribing state.
  useEffect(() => {
    const ids = videoIdsKey ? videoIdsKey.split(',') : [];
    if (ids.length === 0) return undefined;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    async function poll() {
      const resp = await service.getClipTranscripts(ids, false);
      if (stopped) return;
      // Every clip the API knows about, including ones not started yet. Opening a
      // folder now queues a transcript for each of its videos, so "no transcript
      // yet" means "being fetched" and the editor should see that rather than a
      // blank lane. Previously `pending` was excluded, which was right when video
      // transcripts only ever started on an explicit click. Clips that turn out to
      // be silent drop back out once their empty transcript lands — that is
      // captionsWorthShowing's job, not this filter's.
      const discoveredIds = resp
        ? ids.filter((fileId) => resp.transcripts[fileId]?.status != null)
        : [];
      if (resp) setTranscripts((current) => ({ ...current, ...resp.transcripts }));
      // Re-ask for any clip the editor has turned transcription ON for that is
      // still unstarted. The discovery call above deliberately passes start=false
      // so opening a project never spends Whisper on every clip — but that meant a
      // queue job lost between `startVideoTranscript` and the worker was never
      // retried, and the clip sat "waiting" forever. Queueing is deduped by file
      // id on both sides, so re-asking each poll is a cheap no-op once it sticks.
      const unstarted = resp
        ? ids.filter((fileId) => enabledVideoTranscriptIds.has(fileId)
          && resp.transcripts[fileId]?.status === 'pending')
        : [];
      if (unstarted.length) {
        const started = await service.getClipTranscripts(unstarted, true);
        if (stopped) return;
        if (started) setTranscripts((current) => ({ ...current, ...started.transcripts }));
      }
      if (discoveredIds.length) {
        setEnabledVideoTranscriptIds((current) => {
          if (discoveredIds.every((fileId) => current.has(fileId))) return current;
          return new Set([...Array.from(current), ...discoveredIds]);
        });
      }
      const activeIds = new Set([
        ...Array.from(enabledVideoTranscriptIds),
        ...discoveredIds,
      ].filter((fileId) => ids.includes(fileId)));
      const done = resp && Array.from(activeIds).every((fileId) => {
        const status = resp.transcripts[fileId]?.status;
        return status === 'ready' || status === 'error';
      });
      if (activeIds.size > 0 && !done) timer = setTimeout(poll, 3000);
    }
    poll();
    return () => { stopped = true; if (timer) clearTimeout(timer); };
  }, [enabledVideoTranscriptIdsKey, transcriptPollVersion, videoIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  async function startVideoTranscript(fileId: string) {
    setEnabledVideoTranscriptIds((current) => new Set(current).add(fileId));
    setTranscripts((current) => ({
      ...current,
      [fileId]: { ...current[fileId], status: 'pending', error: undefined },
    }));
    const resp = await service.getClipTranscripts([fileId], true);
    if (!resp) {
      setTranscripts((current) => ({
        ...current,
        [fileId]: { ...current[fileId], status: 'error', error: 'The video transcription could not be started.' },
      }));
      return;
    }
    setTranscripts((current) => ({ ...current, ...resp.transcripts }));
    setTranscriptPollVersion((current) => current + 1);
  }

  async function retryTranscript(fileId: string) {
    setRetryingTranscriptIds((current) => new Set(current).add(fileId));
    setTranscripts((current) => ({
      ...current,
      [fileId]: { ...current[fileId], status: 'transcribing', error: undefined },
    }));
    try {
      await service.retryClipTranscript(fileId);
      setTranscriptPollVersion((current) => current + 1);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'The transcription retry could not be started.';
      setTranscripts((current) => ({
        ...current,
        [fileId]: { ...current[fileId], status: 'error', error: message },
      }));
    } finally {
      setRetryingTranscriptIds((current) => {
        const next = new Set(current);
        next.delete(fileId);
        return next;
      });
    }
  }

  // Auto-trim empty sound: once a clip's detected bounds arrive, tighten timeline
  // audio clips still at their full (untrimmed) extent so each starts where the
  // sound starts and ends where it ends. Each clip instance is considered exactly
  // once (so a manual re-expand is never fought), and the trim goes through
  // history — undoable and autosaved like any edit.
  const autoTrimSeen = useRef(new Set<string>());
  function autoTrimSilence(tr: Record<string, ClipTranscript>) {
    if (!latestPlanRef.current) return;
    const current = history.current();
    let changed = false;
    const audioTracks = current.audioTracks.map((track) => ({
      ...track,
      clips: track.clips.map((c) => {
        if (autoTrimSeen.current.has(c.id)) return c;
        const info = tr[c.sourceFileId];
        if (!info || info.status !== 'ready') return c;
        autoTrimSeen.current.add(c.id); // decision is final once the transcript is in
        const start = info.audioStartSec ?? 0;
        const end = info.audioEndSec != null ? info.audioEndSec : c.sourceOutSec;
        // Only clips still at their default extent (in at 0; never widen a user trim).
        if (c.sourceInSec !== 0) return c;
        const newIn = Math.max(0, Math.min(start, c.sourceOutSec - 0.2));
        const newOut = Math.max(newIn + 0.2, Math.min(end, c.sourceOutSec));
        if (newIn < 0.05 && newOut > c.sourceOutSec - 0.05) return c; // nothing to trim
        changed = true;
        return { ...c, sourceInSec: newIn, sourceOutSec: newOut };
      }),
    }));
    if (changed) setTimeline((t) => ({ ...t, audioTracks }));
  }

  // Undo/redo over the timeline document. It is the single writer of plan.timeline:
  // every mutation records a snapshot, and undo/redo apply one back via setPlan (so
  // autosave persists the restored state). History is per-plan and survives reloads.
  const history = useEditHistory<EditorTimeline>(
    HISTORY_KEY_PREFIX,
    (t) => setPlan((p) => (p ? { ...p, timeline: t } : p)),
    emptyTimeline(),
  );
  const { set: histSet, undo: histUndo, redo: histRedo, current: histCurrent, canUndo, canRedo } = history;
  const hasPlan = !!plan;

  const doUndo = () => { histUndo(); setSelectedId(null); };
  const doRedo = () => { histRedo(); setSelectedId(null); };

  // Keyboard shortcuts — but never inside a text field (let native editing work there).
  useEffect(() => {
    if (!hasPlan) return undefined;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const tag = (el?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || el?.isContentEditable) return;
      const k = e.key.toLowerCase();

      if ((k === 'delete' || k === 'backspace') && selectedId) {
        const current = histCurrent();
        const next = removeClipById(current, selectedId);
        if (next !== current) {
          e.preventDefault();
          histSet(next);
          setSelectedId(null);
        }
        return;
      }

      if (!(e.metaKey || e.ctrlKey)) return;
      if (k === 'z' && !e.shiftKey) { e.preventDefault(); histUndo(); setSelectedId(null); }
      else if ((k === 'z' && e.shiftKey) || k === 'y') { e.preventDefault(); histRedo(); setSelectedId(null); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hasPlan, histSet, histUndo, histRedo, histCurrent, selectedId]);

  // Signature of audio coverage over the video track: the audio clips' spans and
  // each main clip's placed span. It deliberately excludes muteAudio so a manual
  // mute toggle doesn't re-trigger the auto-sync below (the manual choice sticks
  // until coverage actually changes).
  const audioCoverageKey = useMemo(() => JSON.stringify({
    a: timeline.audioTracks.flatMap((t) => t.clips.map((c) => [Math.round(c.timelineStartSec * 20), Math.round((c.timelineStartSec + clipDur(c)) * 20)])),
    v: layout.map((l) => [l.clip.id, Math.round(l.start * 20), Math.round(l.end * 20)]),
  }), [timeline, layout]);
  // Auto mute/unmute + show-own-audio. When audio coverage changes: a main video
  // clip covered by an audio track is muted (the track carries the sound); an
  // uncovered clip is un-muted so its own audio plays, and its transcript is
  // enabled so that clip shows its own audio (captions) in its lane.
  useEffect(() => {
    if (!plan) return;
    const current = histCurrent();
    const laid = videoLayout(current);
    let changed = false;
    const nextClips = current.videoTrack.clips.map((clip) => {
      const laidClip = laid.find((x) => x.clip.id === clip.id);
      if (!laidClip) return clip;
      const covered = audioCoversSpan(current, laidClip.start, laidClip.end);
      if (!!clip.muteAudio === covered) return clip;
      changed = true;
      return { ...clip, muteAudio: covered };
    });
    if (changed) history.set({ ...current, videoTrack: { clips: nextClips } });
    // Show the clip's own audio for uncovered clips: enable their transcripts.
    const uncoveredSources = laid
      .filter((l) => !audioCoversSpan(current, l.start, l.end))
      .map((l) => l.clip.sourceFileId);
    if (uncoveredSources.length) {
      setEnabledVideoTranscriptIds((prev) => {
        const next = new Set(prev);
        let added = false;
        uncoveredSources.forEach((id) => { if (!next.has(id)) { next.add(id); added = true; } });
        return added ? next : prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioCoverageKey]);

  // ── Plan load / create / autosave ──
  async function reloadPlans() {
    const resp = await service.listEditPlans(
      source.sourceType === 'STORYBOARD' ? { storyboardId: source.storyboardId } : { folderId: source.folderId },
    );
    setPlans(resp?.plans || []);
  }
  // On source change (or any load — see loadNonce): persist any pending edit, reset
  // the editor, then open a plan. If the load requested a specific project
  // (initialPlanId, from the global "Saved projects" picker) open that one;
  // otherwise default to the most recently updated saved plan for the source, so
  // returning to a script drops you straight back into your work.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await flushSave();
      savedSeq.current = editSeq.current;
      if (cancelled) return;
      setPlan(null); setMarkdown(null); setSelectedId(null); setSaveState('idle');
      history.reset(emptyTimeline(), null);
      const resp = await service.listEditPlans(
        source.sourceType === 'STORYBOARD' ? { storyboardId: source.storyboardId } : { folderId: source.folderId },
      );
      if (cancelled) return;
      const list = resp?.plans || [];
      setPlans(list);
      // openPlan fetches by id, so a requested plan opens whether or not it is in
      // this source's list; fall back to newest (updated_at DESC) otherwise.
      if (initialPlanId) openPlan(initialPlanId);
      else if (list.length) openPlan(list[0].planId);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source.folderId, source.storyboardId, loadNonce]);

  // Once a user is picked in the top bar, drop the "select your name" prompt.
  useEffect(() => { if (actor.trim()) setNeedUserToCreate(false); }, [actor]);

  // Autosave: 0.8s after the last change, persist the whole plan. The debounce
  // coalesces rapid edits (typing, dragging); the seq counters keep the "saved"
  // status honest when a newer edit lands while a save is in flight.
  useEffect(() => {
    if (!plan) return undefined;
    if (skipSave.current) { skipSave.current = false; savedSeq.current = editSeq.current; return undefined; }
    const seq = (editSeq.current += 1);
    setSaveState('saving');
    const t = setTimeout(async () => {
      const resp = await service.updateEditPlan(plan.planId, planPayload(plan));
      if (resp) {
        if (seq > savedSeq.current) savedSeq.current = seq;
        if (!isDirty()) setSaveState('saved');
      } else {
        setSaveState('error');
      }
    }, 800);
    return () => clearTimeout(t);
  }, [plan]); // eslint-disable-line react-hooks/exhaustive-deps

  // Don't lose the last edit: warn before a tab close/refresh with unsaved changes,
  // and flush a pending save when the editor unmounts (e.g. switching source).
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty()) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      if (isDirty() && latestPlanRef.current) {
        service.updateEditPlan(latestPlanRef.current.planId, planPayload(latestPlanRef.current));
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist any pending edit immediately — used before switching plans and to retry
  // a failed save. Awaitable so a caller can flush before navigating away.
  async function flushSave() {
    if (!isDirty() || !latestPlanRef.current) return;
    const p = latestPlanRef.current;
    const seq = editSeq.current;
    setSaveState('saving');
    const resp = await service.updateEditPlan(p.planId, planPayload(p));
    if (resp) { if (seq > savedSeq.current) savedSeq.current = seq; if (!isDirty()) setSaveState('saved'); }
    else setSaveState('error');
  }

  async function openPlan(planId: string) {
    await flushSave();
    setBusy(true); setMarkdown(null); setSelectedId(null);
    previewRef.current?.pause();
    const resp = await service.getEditPlanById(planId);
    skipSave.current = true;
    setPlan(resp?.plan || null);
    if (resp?.plan) history.reset(resp.plan.timeline, resp.plan.planId);
    setSaveState('idle');
    setBusy(false);
  }

  async function createPlan(initialTimeline: EditorTimeline = emptyTimeline()): Promise<EditPlan | null> {
    // Require a current user so every project is attributed (the backend also
    // rejects a create with no createdBy). Surface a clear prompt rather than
    // silently failing the create/auto-create.
    if (!actor.trim()) { setNeedUserToCreate(true); return null; }
    setNeedUserToCreate(false);
    await flushSave();
    setBusy(true); setMarkdown(null);
    const resp = await service.createEditPlan({
      name: source.label ? `Edit — ${source.label}` : 'Untitled edit',
      sourceType: source.sourceType,
      driveFolderId: source.folderId,
      driveFolderUrl: source.folderUrl,
      storyboardId: source.storyboardId,
      deliveryId: source.deliveryId,
      timeline: initialTimeline,
    });
    skipSave.current = true;
    setPlan(resp?.plan || null);
    if (resp?.plan) history.reset(resp.plan.timeline, resp.plan.planId);
    setSaveState('idle');
    setBusy(false);
    reloadPlans();
    return resp?.plan || null;
  }

  async function removePlan() {
    if (!plan) return;
    setDeletePlanDialogOpen(false);
    const removedId = plan.planId;
    setBusy(true);
    previewRef.current?.pause();
    await service.deleteEditPlan(removedId);
    setPlan(null); setMarkdown(null); setSelectedId(null); setBusy(false);
    history.clear(removedId);
    history.reset(emptyTimeline(), null);
    reloadPlans();
  }

  // ── Timeline mutations ──
  // All timeline edits go through history (which applies + records + persists).
  const setTimeline = (fn: (t: EditorTimeline) => EditorTimeline) => { if (plan) history.set(fn(history.current())); };

  const updateVideoClip = (id: string, fn: (c: TLVideoClip) => TLVideoClip) =>
    setTimeline((t) => ({ ...t, videoTrack: { clips: t.videoTrack.clips.map((c) => (c.id === id ? fn(c) : c)) } }));

  const updateOverlayClip = (id: string, fn: (c: TLOverlayClip) => TLOverlayClip) =>
    setTimeline((t) => ({ ...t, overlayTrack: { clips: overlayClips(t).map((c) => (c.id === id ? fn(c) : c)) } }));

  const updateAudioClip = (trackId: string, id: string, fn: (c: TLAudioClip) => TLAudioClip) =>
    setTimeline((t) => ({
      ...t,
      audioTracks: t.audioTracks.map((tr) => (tr.id === trackId ? { ...tr, clips: tr.clips.map((c) => (c.id === id ? fn(c) : c)) } : tr)),
    }));

  const deleteTimelineClip = (id: string) => {
    setTimeline((t) => removeClipById(t, id));
    setSelectedId((current) => (current === id ? null : current));
  };

  // Set the editor note on whichever clip kind is currently selected. Notes
  // persist in the timeline JSON and are rendered into the generated instructions.
  const setSelectedClipNote = (note: string) => {
    if (selVideo) updateVideoClip(selVideo.id, (c) => ({ ...c, note }));
    else if (selOverlay) updateOverlayClip(selOverlay.id, (c) => ({ ...c, note }));
    else if (selAudio) updateAudioClip(selAudio.trackId, selAudio.clip.id, (c) => ({ ...c, note }));
  };

  const addAudioTrack = () => setTimeline((t) => ({ ...t, audioTracks: [...t.audioTracks, newAudioTrack(`Audio ${t.audioTracks.length + 1}`)] }));
  // One audio track for now, so the row is either offered or removable, never both.
  const hasAudioTrack = timeline.audioTracks.length > 0;
  // Removing the track takes its clips with it, which is why it asks first: the clips
  // are only placements, but re-adding them by hand is real work. Undo covers it either
  // way (this goes through history like every other edit).
  //
  // Removes ONE track — the last, which is the one clips land on (see addClipToTrack and
  // addSegmentToTrack). The cap above only hides the add BUTTON; it does not migrate
  // plans saved when tracks were unlimited, so a plan can still open with two. Clearing
  // the whole array there would delete more than the prompt counted, and leave a clip
  // selected that no longer exists.
  const removeAudioTrack = () => {
    const track = timeline.audioTracks[timeline.audioTracks.length - 1];
    if (!track) return;
    const count = track.clips.length;
    const ok = count === 0 || window.confirm(
      `Remove the audio track and the ${count} clip${count === 1 ? '' : 's'} on it? You can undo this.`,
    );
    if (!ok) return;
    const removedIds = new Set(track.clips.map((clip) => clip.id));
    setTimeline((t) => ({ ...t, audioTracks: t.audioTracks.slice(0, -1) }));
    setSelectedId((id) => (id && removedIds.has(id) ? null : id));
  };
  const openNotesDialog = () => {
    setNotesDraft(plan?.notes || '');
    setNotesDialogOpen(true);
  };
  const saveNotes = () => {
    setPlan((current) => (current ? { ...current, notes: notesDraft } : current));
    setNotesDialogOpen(false);
  };

  // Trim a fresh audio clip to its source's detected sound bounds, when the
  // transcript is already in (otherwise autoTrimSilence catches it on arrival).
  function trimToKnownBounds(clip: TLAudioClip): TLAudioClip {
    const info = transcripts[clip.sourceFileId];
    if (!info || info.status !== 'ready') return clip;
    const newIn = Math.max(0, Math.min(info.audioStartSec ?? 0, clip.sourceOutSec - 0.2));
    const end = info.audioEndSec != null ? info.audioEndSec : clip.sourceOutSec;
    const newOut = Math.max(newIn + 0.2, Math.min(end, clip.sourceOutSec));
    autoTrimSeen.current.add(clip.id);
    return { ...clip, sourceInSec: newIn, sourceOutSec: newOut };
  }

  // Append clips to a timeline: video → video track, audio → last audio track
  // (creating one if needed). Pure — returns a new timeline.
  function appendClips(base: EditorTimeline, newClips: VideoClip[]): EditorTimeline {
    let vt = base.videoTrack.clips.slice();
    const tracks = base.audioTracks.map((tr) => ({ ...tr, clips: tr.clips.slice() }));
    // Deduplicates only WITHIN this call — seeding a new project from a folder listing
    // shouldn't place the same file twice — while leaving clips already on the timeline
    // free to gain another copy.
    const seenInThisCall = new Set<string>();
    for (const src of newClips) {
      if (seenInThisCall.has(src.fileId)) continue;
      seenInThisCall.add(src.fileId);
      if (src.mimeType.startsWith('audio/')) {
        if (tracks.length === 0) tracks.push(newAudioTrack('Audio 1'));
        const tr = tracks[tracks.length - 1];
        tr.clips = [...tr.clips, trimToKnownBounds(audioClipFromSource(src, audioTrackEnd(tr)))];
      } else {
        vt = [...vt, videoClipFromSource(src)];
      }
    }
    const associatedIds = planSourceClipIds(base, clips);
    return {
      ...base,
      sourceClipIds: Array.from(new Set([...associatedIds, ...newClips.map((clip) => clip.fileId)])),
      videoTrack: { clips: vt },
      audioTracks: tracks,
    };
  }

  // Drive reports no duration for audio (only video), so an audio clip would be
  // built zero-length (invisible). Probe the real duration from the media before
  // building; timelineModel still falls back to a default if the probe fails.
  async function resolveDurations(list: VideoClip[]): Promise<VideoClip[]> {
    return Promise.all(list.map(async (c) => {
      if (c.mimeType.startsWith('audio/') && (c.durationSec == null || !isFinite(c.durationSec) || c.durationSec <= 0)) {
        const d = await probeDurationSec(c.streamUrl);
        return d ? { ...c, durationSec: d } : c;
      }
      return c;
    }));
  }

  // Imperative entry from the clip browser. With no plan yet, create one already
  // holding these clips (so they persist at create — avoids the create-then-append
  // autosave race). Otherwise append and let autosave pick it up.
  async function addClips(newClips: VideoClip[]) {
    // Only the in-flight guard remains: a clip already on the timeline can be added
    // again (the same shot is often used more than once), but a double-click during the
    // duration probe should still land one copy rather than two.
    const addable = newClips.filter((clip) => !pendingSourceAdds.current.has(clip.fileId));
    if (!addable.length) return;
    addable.forEach((clip) => pendingSourceAdds.current.add(clip.fileId));
    try {
      setBusy(true);
      const resolved = await resolveDurations(addable);
      setBusy(false);
      if (!plan) { await createPlan(appendClips(emptyTimeline(), resolved)); return; }
      setTimeline((t) => appendClips(t, resolved));
    } finally {
      addable.forEach((clip) => pendingSourceAdds.current.delete(clip.fileId));
      setBusy(false);
    }
  }

  // Add a saved segment: the source clip pre-trimmed to the segment's in/out.
  // Video → video track; audio → last audio track (created if needed).
  // Library b-roll → the B-roll lane, since that is what the lane is for and splicing borrowed
  // footage into the middle of the main track would rewrite the edit.
  async function addSegmentClip(sourceClip: VideoClip, segment: ClipSegment): Promise<boolean> {
    if (pendingSegmentAdds.current.has(segment.segmentId)) return false;
    pendingSegmentAdds.current.add(segment.segmentId);
    try {
      const current = plan ? histCurrent() : emptyTimeline();
      const materialized = {
        ...current,
        clipSegments: planClipSegments(current, legacyClipSegments),
      };
      const added = isBrollSegmentId(segment.segmentId)
        ? addBrollToOverlayTrack(materialized, sourceClip, segment)
        : addSegmentClipToTimeline(materialized, sourceClip, segment);
      if (added === materialized) return false;
      const associatedIds = planSourceClipIds(materialized, clips);
      const next = {
        ...added,
        sourceClipIds: sourceIdsAfterPlacing(associatedIds, segment, sourceClip.fileId),
      };
      if (!plan) await createPlan(next);
      else history.set(next);
      return true;
    } finally {
      pendingSegmentAdds.current.delete(segment.segmentId);
    }
  }

  // Save a range directly into the active plan and add it to that plan's
  // timeline. The id is created client-side because the whole plan document is
  // the persistence boundary; no folder-global segment row is created.
  async function saveSegment(input: NewClipSegment): Promise<ClipSegment | null> {
    const sourceClip = clipsById.get(input.sourceFileId);
    if (!sourceClip) return null;
    const segment: ClipSegment = {
      ...input,
      segmentId: uid(),
      folderId: source.folderId,
      createdAt: new Date().toISOString(),
    };
    const current = plan ? histCurrent() : emptyTimeline();
    const withSegment: EditorTimeline = {
      ...current,
      clipSegments: [...planClipSegments(current, legacyClipSegments), segment],
    };
    const added = addSegmentClipToTimeline(withSegment, sourceClip, segment);
    const next: EditorTimeline = {
      ...added,
      sourceClipIds: Array.from(new Set([...planSourceClipIds(current, clips), sourceClip.fileId])),
    };
    if (!plan) {
      const created = await createPlan(next);
      if (!created) return null;
    } else {
      history.set(next);
    }
    return segment;
  }

  function deleteSegment(segmentId: string): boolean {
    if (!plan) return false;
    const current = histCurrent();
    const segmentsForPlan = planClipSegments(current, legacyClipSegments);
    if (!segmentsForPlan.some((segment) => segment.segmentId === segmentId)) return false;
    const withoutTimelineClips = removeSegmentClips(current, segmentId);
    history.set({
      ...withoutTimelineClips,
      clipSegments: segmentsForPlan.filter((segment) => segment.segmentId !== segmentId),
    });
    setSelectedId((id) => {
      if (!id) return id;
      const selected = current.videoTrack.clips.find((clip) => clip.id === id)
        || overlayClips(current).find((clip) => clip.id === id)
        || current.audioTracks.flatMap((track) => track.clips).find((clip) => clip.id === id);
      return selected?.segmentId === segmentId ? null : id;
    });
    return true;
  }

  function removeSourceClip(sourceFileId: string): boolean {
    if (!plan) return false;
    const current = histCurrent();
    const next = removeSourceClips(current, sourceFileId);
    if (next === current) return false;
    history.set(next);
    setSelectedId((id) => {
      if (!id) return id;
      const selected = current.videoTrack.clips.find((clip) => clip.id === id)
        || overlayClips(current).find((clip) => clip.id === id)
        || current.audioTracks.flatMap((track) => track.clips).find((clip) => clip.id === id);
      return selected && !selected.segmentId && selected.sourceFileId === sourceFileId ? null : id;
    });
    return true;
  }

  function removeClipFromPlan(sourceFileId: string): boolean {
    if (!plan) return false;
    const current = histCurrent();
    const currentSourceClipIds = planSourceClipIds(current, clips);
    const nextSourceClipIds = currentSourceClipIds.filter((fileId) => fileId !== sourceFileId);
    const segmentsForPlan = planClipSegments(current, legacyClipSegments);
    const removedSegmentIds = segmentsForPlan
      .filter((segment) => segment.sourceFileId === sourceFileId)
      .map((segment) => segment.segmentId);
    let withoutTimelineClip = removeSourceClips(current, sourceFileId);
    removedSegmentIds.forEach((segmentId) => {
      withoutTimelineClip = removeSegmentClips(withoutTimelineClip, segmentId);
    });
    if (nextSourceClipIds.length === currentSourceClipIds.length
      && removedSegmentIds.length === 0
      && withoutTimelineClip === current) return false;
    history.set({
      ...withoutTimelineClip,
      sourceClipIds: nextSourceClipIds,
      clipSegments: segmentsForPlan.filter((segment) => segment.sourceFileId !== sourceFileId),
    });
    setSelectedId((id) => {
      if (!id) return id;
      const selected = current.videoTrack.clips.find((clip) => clip.id === id)
        || overlayClips(current).find((clip) => clip.id === id)
        || current.audioTracks.flatMap((track) => track.clips).find((clip) => clip.id === id);
      return selected?.sourceFileId === sourceFileId ? null : id;
    });
    return true;
  }

  // Add a source clip to the plan's Clips-tab membership without placing it on
  // the timeline (e.g. a freshly extracted audio file). With no open plan the
  // Clips tab already shows the folder's full inventory, so there is nothing to
  // record and the add is reported as done.
  function addSourceToPlan(sourceFileId: string): boolean {
    if (!plan) return true;
    const current = histCurrent();
    const currentSourceClipIds = planSourceClipIds(current, clips);
    if (currentSourceClipIds.includes(sourceFileId)) return true;
    history.set({ ...current, sourceClipIds: [...currentSourceClipIds, sourceFileId] });
    return true;
  }

  // Add a source clip as a B-roll cutaway. With a target main-track clip id it
  // aligns to that clip's start and caps to its length; without one it drops at
  // the current playhead (full source length) for the user to position/trim.
  // No used-source dedupe — B-roll often reuses a source already on the main track.
  function addOverlayClip(sourceClip: VideoClip, targetVideoClipId?: string | null, startSecOverride?: number): boolean {
    if (!plan) return false;
    const current = histCurrent();
    let startSec = Math.max(0, startSecOverride ?? playheadRef.current ?? 0);
    let maxDur: number | undefined;
    if (targetVideoClipId) {
      const target = videoLayout(current).find((entry) => entry.clip.id === targetVideoClipId);
      if (target) { startSec = target.start; maxDur = clipDur(target.clip); }
    }
    const clip = overlayClipFromSource(sourceClip, startSec, maxDur);
    history.set({
      ...current,
      sourceClipIds: Array.from(new Set([...planSourceClipIds(current, clips), sourceClip.fileId])),
      // The row holds one clip at a time: the cutaway lands where it was dropped, and
      // whatever it intersects moves later.
      overlayTrack: { clips: resolveOverlayOverlaps([...overlayClips(current), clip], clip.id) },
    });
    setSelectedId(clip.id);
    return true;
  }

  // A library clip dropped onto a track at a specific time.
  //  • audio source → placed on an audio track at the drop time (free position)
  //  • video source on the B-roll lane → cutaway at the drop time
  //  • video source on the main track → INSERTED at the drop point, pushing the
  //    clips after it later (the main track is sequential, so a mid-track drop
  //    splices in rather than appends)
  // A saved sub-clip dropped onto a track. Same placement rules as a whole clip,
  // but the timeline clip carries the segment's own trim (and its id, so the Clips
  // tab can tell which saved clip is on the timeline).
  function addSegmentToTrack(segmentId: string, track: 'video' | 'overlay' | 'audio', dropTime: number) {
    if (!plan) return;
    const segment = ownedClipSegments.find((s) => s.segmentId === segmentId);
    const sourceClip = segment ? clipsById.get(segment.sourceFileId) : undefined;
    if (!segment || !sourceClip) return;
    const current = histCurrent();
    const at = Math.max(0, dropTime);
    const trim = {
      segmentId: segment.segmentId,
      fileName: segment.name || sourceClip.name,
      sourceInSec: segment.inSec,
      sourceOutSec: segment.outSec,
    };
    const sourceClipIds = sourceIdsAfterPlacing(planSourceClipIds(current, clips), segment, sourceClip.fileId);

    // A saved sub-clip of an audio file belongs on an audio track. This branch was
    // missing, so dragging one in placed it on the VIDEO track as a silent clip with
    // no picture — the segment's own `kind` was never consulted. Matches the whole-clip
    // path in addClipToTrack, including creating the first audio track on demand.
    if (segment.kind === 'audio') {
      const tracks = current.audioTracks.length ? current.audioTracks.slice() : [newAudioTrack('Audio 1')];
      const last = tracks[tracks.length - 1];
      const clip = { ...audioClipFromSource(sourceClip, at), ...trim, timelineStartSec: at };
      tracks[tracks.length - 1] = { ...last, clips: [...last.clips, clip] };
      history.set({ ...current, audioTracks: tracks, sourceClipIds });
      setSelectedId(clip.id);
      return;
    }

    if (track === 'overlay') {
      const clip = { ...overlayClipFromSource(sourceClip, at), ...trim };
      history.set({
        ...current,
        sourceClipIds,
        // Same one-clip-at-a-time rule as a whole-clip cutaway (see addOverlayClip).
        overlayTrack: { clips: resolveOverlayOverlaps([...overlayClips(current), clip], clip.id) },
      });
      setSelectedId(clip.id);
      return;
    }
    const laid = videoLayout(current);
    let idx = current.videoTrack.clips.length;
    for (let i = 0; i < laid.length; i += 1) {
      if (at < (laid[i].start + laid[i].end) / 2) { idx = i; break; }
    }
    const nextClips = current.videoTrack.clips.slice();
    nextClips.splice(idx, 0, { ...videoClipFromSource(sourceClip), ...trim });
    history.set({ ...current, videoTrack: { clips: nextClips }, sourceClipIds });
  }

  async function addClipToTrack(sourceFileId: string, track: 'video' | 'overlay' | 'audio', dropTime: number) {
    const sourceClip = clipsById.get(sourceFileId);
    if (!sourceClip) return;
    const at = Math.max(0, dropTime);

    if (sourceClip.mimeType.startsWith('audio/')) {
      // Drive reports no duration for audio (videoMediaMetadata is video-only), so the
      // clip has to be fetched far enough to read its length before it can be laid out
      // — a clip of unknown length has no width. That network round trip is the pause
      // between dropping and seeing the clip appear; show it rather than looking hung.
      // (The "Add to timeline" button already sets busy around the same probe.)
      setBusy(true);
      try {
        const [resolved] = await resolveDurations([sourceClip]);
        const place = (current: EditorTimeline): EditorTimeline => {
          const tracks = current.audioTracks.length ? current.audioTracks.slice() : [newAudioTrack('Audio 1')];
          const last = tracks[tracks.length - 1];
          tracks[tracks.length - 1] = { ...last, clips: [...last.clips, { ...audioClipFromSource(resolved, at), timelineStartSec: at }] };
          return { ...current, audioTracks: tracks, sourceClipIds: Array.from(new Set([...planSourceClipIds(current, clips), sourceFileId])) };
        };
        if (!plan) await createPlan(place(emptyTimeline()));
        else history.set(place(histCurrent()));
      } finally {
        setBusy(false);
      }
      return;
    }

    if (track === 'overlay') { addOverlayClip(sourceClip, null, at); return; }

    // Main video track: insert at the drop point.
    if (!plan) { await createPlan(appendClips(emptyTimeline(), [sourceClip])); return; }
    const current = histCurrent();
    const laid = videoLayout(current);
    let idx = current.videoTrack.clips.length;
    for (let i = 0; i < laid.length; i += 1) {
      if (at < (laid[i].start + laid[i].end) / 2) { idx = i; break; }
    }
    const nextClips = current.videoTrack.clips.slice();
    nextClips.splice(idx, 0, videoClipFromSource(sourceClip));
    history.set({
      ...current,
      videoTrack: { clips: nextClips },
      sourceClipIds: Array.from(new Set([...planSourceClipIds(current, clips), sourceFileId])),
    });
  }

  // Timeline time under a client X coordinate (accounts for the ruler start
  // offset, current zoom, and horizontal scroll) — used to place a dropped clip.
  function timelineTimeFromClientX(clientX: number): number {
    const root = timelineViewportRef.current;
    const canvas = root?.querySelector('.timeline-editor') as HTMLElement | null;
    const scrollGrid = root?.querySelector('.timeline-editor-edit-area .ReactVirtualized__Grid') as HTMLElement | null;
    const rect = canvas?.getBoundingClientRect();
    const pointerX = rect ? clientX - rect.left : 0;
    const scrollLeft = scrollGrid?.scrollLeft || 0;
    return Math.max(0, (scrollLeft + pointerX - TIMELINE_START_LEFT) / zoom);
  }

  // Which lane a drag is over, by the cursor's Y against the actual B-roll row's
  // box — more reliable than e.target.closest during HTML5 drag (the event target
  // is often a stale/wrong node, so the B-roll lane never highlighted before).
  function laneAtClientY(clientY: number): 'video' | 'overlay' {
    const root = timelineViewportRef.current;
    const overlayRow = root?.querySelector('.lane-overlay') as HTMLElement | null;
    if (!overlayRow) return 'video';
    const videoRow = root?.querySelector('.lane-video') as HTMLElement | null;
    return nearestLane(
      clientY,
      overlayRow.getBoundingClientRect(),
      videoRow?.getBoundingClientRect() || null,
    );
  }

  function removeSegmentClip(segmentId: string): boolean {
    if (!plan) return false;
    const current = histCurrent();
    const next = removeSegmentClips(current, segmentId);
    if (next === current) return false;
    history.set(next);
    setSelectedId((id) => {
      if (!id) return id;
      const selected = current.videoTrack.clips.find((clip) => clip.id === id)
        || overlayClips(current).find((clip) => clip.id === id)
        || current.audioTracks.flatMap((track) => track.clips).find((clip) => clip.id === id);
      return selected?.segmentId === segmentId ? null : id;
    });
    return true;
  }

  function findTimelineClipForSource(sourceFileId: string) {
    const current = histCurrent();
    const selectedVideo = current.videoTrack.clips.find((clip) => !clip.segmentId && clip.id === selectedId && clip.sourceFileId === sourceFileId);
    if (selectedVideo) return { kind: 'video' as const, clip: selectedVideo };
    const selectedOverlay = overlayClips(current).find((clip) => !clip.segmentId && clip.id === selectedId && clip.sourceFileId === sourceFileId);
    if (selectedOverlay) return { kind: 'overlay' as const, clip: selectedOverlay };
    for (const track of current.audioTracks) {
      const selectedAudio = track.clips.find((clip) => !clip.segmentId && clip.id === selectedId && clip.sourceFileId === sourceFileId);
      if (selectedAudio) return { kind: 'audio' as const, clip: selectedAudio, trackId: track.id };
    }
    const firstVideo = current.videoTrack.clips.find((clip) => !clip.segmentId && clip.sourceFileId === sourceFileId);
    if (firstVideo) return { kind: 'video' as const, clip: firstVideo };
    const firstOverlay = overlayClips(current).find((clip) => !clip.segmentId && clip.sourceFileId === sourceFileId);
    if (firstOverlay) return { kind: 'overlay' as const, clip: firstOverlay };
    for (const track of current.audioTracks) {
      const firstAudio = track.clips.find((clip) => !clip.segmentId && clip.sourceFileId === sourceFileId);
      if (firstAudio) return { kind: 'audio' as const, clip: firstAudio, trackId: track.id };
    }
    return null;
  }

  function selectClipForSource(sourceFileId: string) {
    const match = findTimelineClipForSource(sourceFileId);
    setSelectedId(match?.clip.id ?? null);
  }

  function findTimelineClipForSegment(segmentId: string) {
    const current = histCurrent();
    const video = current.videoTrack.clips.find((clip) => clip.segmentId === segmentId);
    if (video) return { kind: 'video' as const, clip: video };
    const overlay = overlayClips(current).find((clip) => clip.segmentId === segmentId);
    if (overlay) return { kind: 'overlay' as const, clip: overlay };
    for (const track of current.audioTracks) {
      const audio = track.clips.find((clip) => clip.segmentId === segmentId);
      if (audio) return { kind: 'audio' as const, clip: audio, trackId: track.id };
    }
    return null;
  }

  function selectClipForSegment(segmentId: string) {
    const match = findTimelineClipForSegment(segmentId);
    setSelectedId(match?.clip.id ?? null);
  }

  function trimSourceClip(sourceFileId: string, inSec: number, outSec: number) {
    const sourceClip = clipsById.get(sourceFileId);
    if (!sourceClip) return false;
    const sourceDuration = sourceLength(sourceClip);
    const nextIn = Math.max(0, inSec);
    const nextOut = Math.max(nextIn + 0.1, sourceDuration == null ? outSec : Math.min(outSec, sourceDuration));
    const match = plan ? findTimelineClipForSource(sourceFileId) : null;
    if (match) {
      if (match.kind === 'video') {
        updateVideoClip(match.clip.id, (clip) => ({ ...clip, sourceInSec: nextIn, sourceOutSec: nextOut }));
      } else if (match.kind === 'overlay') {
        updateOverlayClip(match.clip.id, (clip) => ({ ...clip, sourceInSec: nextIn, sourceOutSec: nextOut }));
      } else {
        updateAudioClip(match.trackId, match.clip.id, (clip) => ({ ...clip, sourceInSec: nextIn, sourceOutSec: nextOut }));
      }
      setSelectedId(match.clip.id);
      return true;
    }
    // Not on the timeline yet — add the clip (to the main video track, or the
    // last audio track for audio sources) with this trim range so trimming works
    // straight from the clip library, then select the freshly added clip.
    const place = (base: EditorTimeline): { timeline: EditorTimeline; newId: string } => {
      const added = appendClips(base, [sourceClip]);
      if (sourceClip.mimeType.startsWith('audio/')) {
        const tracks = added.audioTracks.map((tr) => ({ ...tr, clips: tr.clips.slice() }));
        const lastTrack = tracks[tracks.length - 1];
        const last = lastTrack.clips[lastTrack.clips.length - 1];
        lastTrack.clips[lastTrack.clips.length - 1] = { ...last, sourceInSec: nextIn, sourceOutSec: nextOut };
        return { timeline: { ...added, audioTracks: tracks }, newId: last.id };
      }
      const vt = added.videoTrack.clips.slice();
      const last = vt[vt.length - 1];
      vt[vt.length - 1] = { ...last, sourceInSec: nextIn, sourceOutSec: nextOut };
      return { timeline: { ...added, videoTrack: { clips: vt } }, newId: last.id };
    };
    if (!plan) {
      void createPlan(place(emptyTimeline()).timeline);
      return true;
    }
    const { timeline: next, newId } = place(histCurrent());
    history.set(next);
    setSelectedId(newId);
    return true;
  }

  // Retarget a saved sub-clip's range plus every timeline piece that carries it.
  // Selection is deliberately not touched here — trimSegmentClip (the Clips-tab
  // entry) selects the trimmed clip, while a companion trim keeps the current one.
  function applySegmentTrim(segmentId: string, inSec: number, outSec: number): boolean {
    if (!plan) return false;
    const current = histCurrent();
    const segmentsForPlan = planClipSegments(current, legacyClipSegments);
    const segment = segmentsForPlan.find((candidate) => candidate.segmentId === segmentId);
    if (!segment) return false;
    const sourceDuration = sourceLength(clipsById.get(segment.sourceFileId));
    const nextIn = Math.max(0, inSec);
    const nextOut = Math.max(nextIn + 0.1, sourceDuration == null ? outSec : Math.min(outSec, sourceDuration));
    const updateRange = <T extends TLVideoClip | TLOverlayClip | TLAudioClip>(clip: T): T => (
      clip.segmentId === segmentId ? { ...clip, sourceInSec: nextIn, sourceOutSec: nextOut } : clip
    );
    history.set({
      ...current,
      clipSegments: segmentsForPlan.map((candidate) => (
        candidate.segmentId === segmentId ? { ...candidate, inSec: nextIn, outSec: nextOut } : candidate
      )),
      videoTrack: { clips: current.videoTrack.clips.map(updateRange) },
      ...(current.overlayTrack ? { overlayTrack: { clips: current.overlayTrack.clips.map(updateRange) } } : {}),
      audioTracks: current.audioTracks.map((track) => ({ ...track, clips: track.clips.map(updateRange) })),
    });
    return true;
  }

  function trimSegmentClip(segmentId: string, inSec: number, outSec: number) {
    if (!applySegmentTrim(segmentId, inSec, outSec)) return false;
    const match = findTimelineClipForSegment(segmentId);
    if (match) setSelectedId(match.clip.id);
    return true;
  }

  useImperativeHandle(ref, () => ({
    addClips,
    addSegmentClip,
    saveSegment,
    deleteSegment,
    removeSourceClip,
    removeSegmentClip,
    selectClipForSource,
    selectClipForSegment,
    trimSourceClip,
    trimSegmentClip,
    removeClipFromPlan,
    addSourceToPlan,
    addOverlayClip,
    /**
     * Pull a library clip into this plan: the asset record AND the segment that makes it
     * draggable, in one undoable change. Idempotent — the segment id is derived from the label
     * id, so adding the same clip twice can't produce two of anything.
     */
    addBrollAsset: (asset: BrollAsset, segment: ClipSegment) => {
      const current = histCurrent();
      if ((current.brollAssets || []).some(a => a.labelId === asset.labelId)) return;
      // Through planClipSegments, like every other segment write: on a plan saved before
      // segments became plan-owned, `clipSegments` is null and reading it directly would
      // materialize an empty list, dropping that plan's legacy segments.
      const segments = planClipSegments(current, legacyClipSegments);
      history.set({
        ...current,
        brollAssets: [...(current.brollAssets || []), asset],
        clipSegments: segments.some(s => s.segmentId === segment.segmentId)
          ? segments
          : [...segments, segment],
      });
    },
    /**
     * Drop a pulled-in clip from the plan. Its timeline placements go too — leaving them would
     * mean clips on a track whose asset no longer exists, which is how a plan ends up
     * referencing footage nothing can resolve.
     */
    removeBrollAsset: (labelId: string) => {
      const current = histCurrent();
      const segmentId = brollSegmentId(labelId);
      const withoutPlacements = removeSegmentClips(current, segmentId);
      history.set({
        ...withoutPlacements,
        brollAssets: (current.brollAssets || []).filter(a => a.labelId !== labelId),
        clipSegments: planClipSegments(current, legacyClipSegments).filter(s => s.segmentId !== segmentId),
      });
    },
    pausePreview: () => previewRef.current?.pause(),
    clearSelection: () => setSelectedId(null),
    setSelectedClipNote,
  }), [plan, source, selectedId, legacyClipSegments]); // eslint-disable-line react-hooks/exhaustive-deps

  // "New project" starts with ALL of the folder's clips already laid out (video
  // clips in order + audio clips on an audio track), so the editor begins from a
  // full rough cut rather than an empty timeline.
  async function newSeededPlan() {
    setBusy(true);
    const resolved = await resolveDurations(clips);
    await createPlan(appendClips({ ...emptyTimeline(), sourceClipIds: clips.map((clip) => clip.fileId) }, resolved)); // createPlan clears busy
  }

  // ── Split at playhead ──
  async function splitAtPlayhead() {
    if (splitting || !plan) return;
    const t = playheadRef.current;
    const current = histCurrent();
    const currentVideoLayout = videoLayout(current);
    // Overlay clips split as 'video' saved segments — their media is video.
    type SplitTarget =
      | { kind: 'video'; clip: TLVideoClip | TLOverlayClip; start: number; end: number }
      | { kind: 'audio'; clip: TLAudioClip; start: number; end: number };
    const containsPlayhead = (start: number, end: number) => t > start + 0.05 && t < end - 0.05;
    let target: SplitTarget | null = null;

    const selectedVideo = currentVideoLayout.find((entry) => entry.clip.id === selectedId);
    if (selectedVideo && containsPlayhead(selectedVideo.start, selectedVideo.end)) {
      target = { kind: 'video', clip: selectedVideo.clip, start: selectedVideo.start, end: selectedVideo.end };
    }
    if (!target && selectedId) {
      const selectedOverlay = overlayLayout(current).find((entry) => entry.clip.id === selectedId);
      if (selectedOverlay && containsPlayhead(selectedOverlay.start, selectedOverlay.end)) {
        target = { kind: 'video', clip: selectedOverlay.clip, start: selectedOverlay.start, end: selectedOverlay.end };
      }
    }
    if (!target && selectedId) {
      for (const track of current.audioTracks) {
        const clip = track.clips.find((candidate) => candidate.id === selectedId);
        if (!clip) continue;
        const end = clip.timelineStartSec + clipDur(clip);
        if (containsPlayhead(clip.timelineStartSec, end)) {
          target = { kind: 'audio', clip, start: clip.timelineStartSec, end };
        }
        break;
      }
    }
    if (!target) {
      const video = currentVideoLayout.find((entry) => containsPlayhead(entry.start, entry.end));
      if (video) target = { kind: 'video', clip: video.clip, start: video.start, end: video.end };
    }
    if (!target) {
      const overlay = overlayLayout(current).find((entry) => containsPlayhead(entry.start, entry.end));
      if (overlay) target = { kind: 'video', clip: overlay.clip, start: overlay.start, end: overlay.end };
    }
    if (!target) {
      for (const track of current.audioTracks) {
        const clip = track.clips.find((candidate) => (
          containsPlayhead(candidate.timelineStartSec, candidate.timelineStartSec + clipDur(candidate))
        ));
        if (clip) {
          target = {
            kind: 'audio',
            clip,
            start: clip.timelineStartSec,
            end: clip.timelineStartSec + clipDur(clip),
          };
          break;
        }
      }
    }
    if (!target) {
      setSplitError('Move the playhead inside a video or audio clip before splitting.');
      return;
    }

    const sourceClip = clipsById.get(target.clip.sourceFileId);
    if (!sourceClip) {
      setSplitError('The source file for this clip is no longer available.');
      return;
    }
    const splitSec = target.clip.sourceInSec + (t - target.start);
    setSplitting(true);
    setSplitError(null);
    try {
      const createdAt = new Date().toISOString();
      const segmentBase = {
        folderId: source.folderId,
        sourceFileId: target.clip.sourceFileId,
        fileName: sourceClip.name,
        name: target.clip.fileName,
        kind: target.kind,
        createdAt,
      };
      const savedSegments: [ClipSegment, ClipSegment] = [{
        ...segmentBase,
        segmentId: uid(),
        inSec: target.clip.sourceInSec,
        outSec: splitSec,
      }, {
        ...segmentBase,
        segmentId: uid(),
        inSec: splitSec,
        outSec: target.clip.sourceOutSec,
      }];
      const nextTimeline = splitTimelineClipWithSegments(current, target.clip.id, savedSegments);
      if (nextTimeline === current) {
        setSplitError('The selected clip could not be split. Try again.');
        return;
      }
      history.set({
        ...nextTimeline,
        clipSegments: [...planClipSegments(current, legacyClipSegments), ...savedSegments],
      });
      setSelectedId(target.clip.id);
    } finally {
      setSplitting(false);
    }
  }

  // ── Build the library's rows from our model ──
  const segById = useMemo(() => {
    const m = new Map<string, StoryboardSegment>();
    for (const s of segments || []) m.set(s.segmentId, s);
    return m;
  }, [segments]);
  const videoClipById = useMemo(() => {
    const m = new Map<string, TLVideoClip>();
    for (const c of timeline.videoTrack.clips) m.set(c.id, c);
    return m;
  }, [timeline]);
  const overlayClipById = useMemo(() => {
    const m = new Map<string, TLOverlayClip>();
    for (const c of overlayClips(timeline)) m.set(c.id, c);
    return m;
  }, [timeline]);
  const audioClipById = useMemo(() => {
    const m = new Map<string, { clip: TLAudioClip; trackId: string }>();
    for (const tr of timeline.audioTracks) for (const c of tr.clips) m.set(c.id, { clip: c, trackId: tr.id });
    return m;
  }, [timeline]);
  const captionsWorthShowing = (
    clip: TLVideoClip | TLAudioClip,
    transcript: ClipTranscript | undefined,
  ) => hasCaptionsWorthShowing(transcript, clip.sourceInSec, clip.sourceOutSec);

  // Clips whose caption lane would actually render something. Captions are opt-in
  // per clip, but an enabled clip that turns out to be silent shouldn't hold the
  // lane open on its own.
  const captionedVideoClips = timeline.videoTrack.clips.filter((clip) => (
    enabledVideoTranscriptIds.has(clip.sourceFileId)
    && captionsWorthShowing(clip, transcripts[clip.sourceFileId])
  ));
  const captionedVideoClipIds = new Set(captionedVideoClips.map((clip) => clip.id));
  const hasEnabledVideoTranscript = captionedVideoClips.length > 0;

  // The full selectable window within a clip's source: a saved sub-clip
  // (segment) uses the range it was carved as; anything else the whole source
  // file. Null max = source duration unknown (nothing to show or clamp to).
  const sourceWindowOf = (clip: TLVideoClip | TLOverlayClip | TLAudioClip): { minSec: number; maxSec: number | null } => {
    if (clip.segmentId) {
      const segment = ownedClipSegments.find((s) => s.segmentId === clip.segmentId);
      if (segment) return { minSec: segment.inSec, maxSec: segment.outSec };
    }
    return { minSec: 0, maxSec: sourceLength(clipsById.get(clip.sourceFileId)) };
  };

  const editorData: TimelineRow[] = useMemo(() => {
    // The selected clip's untrimmed extent, drawn as an inert dashed band behind
    // its bar so trimming happens against the whole clip right on the timeline.
    const ghostFor = (clip: TLVideoClip | TLOverlayClip | TLAudioClip, clipStart: number): TimelineAction | null => {
      if (clip.id !== selectedId) return null;
      const win = sourceWindowOf(clip);
      if (win.maxSec == null) return null;
      const start = clipStart - (clip.sourceInSec - win.minSec);
      const end = clipStart + (win.maxSec - clip.sourceInSec);
      if (end - start <= clipDur(clip) + 0.05) return null; // fully untrimmed already
      return {
        id: `ghost:${clip.id}`, start: Math.max(0, start), end, effectId: 'ghost',
        movable: false, flexible: false, disable: true,
      };
    };
    const withGhost = (actions: TimelineAction[], ghost: TimelineAction | null) => (ghost ? [ghost, ...actions] : actions);

    const rows: TimelineRow[] = [];
    if (segments && segments.length && showScriptLane) {
      let cursor = 0;
      const actions: TimelineAction[] = segments.map((s) => {
        const start = cursor; cursor += scriptSegLen(s);
        return { id: `seg:${s.segmentId}`, start, end: cursor, effectId: 'script', movable: false, flexible: false, disable: true };
      });
      rows.push({ id: 'script', actions, classNames: ['lane-script'] });
    }
    // B-roll overlay lane sits above the main video track — always present so
    // there is a visible target even before the first cutaway is added.
    const overlayGhost = overlayLaid.map((l) => ghostFor(l.clip, l.start)).find(Boolean) || null;
    rows.push({
      id: 'overlay',
      actions: withGhost(overlayLaid.map((l) => ({
        id: l.clip.id, start: l.start, end: l.end, effectId: 'overlay', movable: true, flexible: true, minStart: 0, selected: l.clip.id === selectedId,
      })), overlayGhost),
      classNames: ['lane-overlay'],
    });
    // Main video clips show their trimmed bounds only (no full-clip ghost) —
    // widening a main clip happens in the clip editor, not on the timeline.
    rows.push({
      id: 'video',
      actions: layout.map((l) => ({
        id: l.clip.id, start: l.start, end: l.end, effectId: 'video', movable: true, flexible: true, minStart: 0, selected: l.clip.id === selectedId,
      })),
      classNames: ['lane-video'],
      rowHeight: ROW_HEIGHT,
    });
    // Video captions ride in their own read-only lane directly beneath the video
    // track — one caption block per clip that has a transcript, positioned under
    // that clip. Keeps the transcript legible instead of overlaying the filmstrip.
    if (hasEnabledVideoTranscript) {
      rows.push({
        id: 'videocaptions',
        actions: layout
          .filter((l) => captionedVideoClipIds.has(l.clip.id))
          .map((l) => ({
            id: `cap:${l.clip.id}`, start: l.start, end: l.end, effectId: 'videocaptions', movable: false, flexible: false, disable: true,
          })),
        classNames: ['lane-videocaptions'],
        rowHeight: videoCaptionRowHeight,
      });
    }
    for (const tr of timeline.audioTracks) {
      const audioGhost = tr.clips.map((c) => ghostFor(c, c.timelineStartSec)).find(Boolean) || null;
      rows.push({
        id: `audio:${tr.id}`,
        actions: withGhost(tr.clips.map((c) => ({
          id: c.id, start: c.timelineStartSec, end: c.timelineStartSec + clipDur(c), effectId: 'audio', movable: true, flexible: true, minStart: 0, selected: c.id === selectedId,
        })), audioGhost),
        classNames: ['lane-audio'],
        rowHeight: audioRowHeight, // label bar + transcript strip, no dead space
      });
    }
    // No phantom row for an audio drag into a project without an audio track. It used to
    // push an empty `audio:pending` row here, which timelineHeight sums — so starting the
    // drag grew the dock by a row and abandoning it shrank it back, and every lane change
    // mid-drag rebuilt editorData through the dropTarget dependency. There is no lane to
    // point at yet, so the whole canvas takes the highlight instead (see the sx below).
    return rows;
    // transcripts don't change the row/action GEOMETRY, but a fresh
    // editorData reference is what makes the library re-render the action DOM —
    // without them, caption toggles/arrivals wouldn't repaint until the next drag.
  }, [audioRowHeight, clipsById, enabledVideoTranscriptIdsKey, hasEnabledVideoTranscript, layout, overlayLaid, ownedClipSegmentsKey, segments, selectedId, showScriptLane, timeline, transcripts, videoCaptionRowHeight]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Library callbacks ──
  function onActionMoveEnd({ action, row, start }: { action: TimelineAction; row: TimelineRow; start: number; end: number }) {
    if (action.id.startsWith('ghost:')) return;
    if (row.id === 'video') {
      // Reorder: drop the clip where its new start lands among the others.
      setTimeline((t) => {
        const clips = t.videoTrack.clips;
        const moved = clips.find((c) => c.id === action.id);
        if (!moved) return t;
        const others = clips.filter((c) => c.id !== action.id);
        let cursor = 0; let idx = others.length;
        for (let i = 0; i < others.length; i += 1) {
          const d = clipDur(others[i]);
          if (start < cursor + d / 2) { idx = i; break; }
          cursor += d;
        }
        others.splice(idx, 0, moved);
        return { ...t, videoTrack: { clips: others } };
      });
    } else if (row.id === 'overlay') {
      const moving = overlayClipById.get(action.id);
      if (!moving) return;
      // Lands where it was dropped; anything it now runs into is pushed later, as the
      // main track does when a clip is inserted ahead of others.
      setTimeline((t) => ({
        ...t,
        overlayTrack: {
          clips: resolveOverlayOverlaps(
            overlayClips(t).map((c) => (c.id === action.id ? { ...c, timelineStartSec: Math.max(0, start) } : c)),
            action.id,
          ),
        },
      }));
    } else if (row.id.startsWith('audio:')) {
      const trackId = row.id.slice('audio:'.length);
      updateAudioClip(trackId, action.id, (c) => ({ ...c, timelineStartSec: Math.max(0, start) }));
    }
  }

  // Edge-drag = trim, clamped to the clip's source window (a saved sub-clip's
  // carved range, otherwise the whole source — the extent the ghost band shows).
  function onActionResizeEnd({ action, row, start, end, dir }: { action: TimelineAction; row: TimelineRow; start: number; end: number; dir: 'left' | 'right' }) {
    if (action.id.startsWith('ghost:')) return;
    const newDur = Math.max(0.1, end - start);
    if (row.id === 'video') {
      const clip = videoClipById.get(action.id);
      if (!clip) return;
      const win = sourceWindowOf(clip);
      if (dir === 'right') {
        let out = clip.sourceInSec + newDur;
        if (win.maxSec != null) out = Math.min(out, win.maxSec);
        updateVideoClip(clip.id, (c) => ({ ...c, sourceOutSec: Math.max(out, c.sourceInSec + 0.1) }));
      } else {
        const inn = Math.max(win.minSec, clip.sourceOutSec - newDur);
        updateVideoClip(clip.id, (c) => ({ ...c, sourceInSec: Math.min(inn, c.sourceOutSec - 0.1) }));
      }
    } else if (row.id === 'overlay') {
      const clip = overlayClipById.get(action.id);
      if (!clip) return;
      const win = sourceWindowOf(clip);
      if (dir === 'right') {
        let out = clip.sourceInSec + newDur;
        if (win.maxSec != null) out = Math.min(out, win.maxSec);
        // Growing a clip displaces what follows, the same as dropping one in front of it.
        setTimeline((t) => ({
          ...t,
          overlayTrack: {
            clips: resolveOverlayOverlaps(
              overlayClips(t).map((c) => (c.id === clip.id
                ? { ...c, sourceOutSec: Math.max(out, c.sourceInSec + 0.1) }
                : c)),
              clip.id,
            ),
          },
        }));
      } else {
        // Dragging the left edge earlier also moves the clip's start, so it is bounded
        // by the previous clip's end rather than only by 0 — pushing that one further
        // back would move the row the wrong way.
        const { prevEnd } = overlayNeighbourBounds(histCurrent(), clip.id);
        const startSec = Math.max(prevEnd, Math.max(0, start));
        const room = (clip.timelineStartSec || 0) + clipDur(clip) - startSec;
        const inn = Math.max(win.minSec, clip.sourceOutSec - Math.min(newDur, room));
        updateOverlayClip(clip.id, (c) => ({ ...c, sourceInSec: Math.min(inn, c.sourceOutSec - 0.1), timelineStartSec: startSec }));
      }
    } else if (row.id.startsWith('audio:')) {
      const entry = audioClipById.get(action.id);
      if (!entry) return;
      const { clip, trackId } = entry;
      const win = sourceWindowOf(clip);
      if (dir === 'right') {
        let out = clip.sourceInSec + newDur;
        if (win.maxSec != null) out = Math.min(out, win.maxSec);
        updateAudioClip(trackId, clip.id, (c) => ({ ...c, sourceOutSec: Math.max(out, c.sourceInSec + 0.1) }));
      } else {
        const inn = Math.max(win.minSec, clip.sourceOutSec - newDur);
        updateAudioClip(trackId, clip.id, (c) => ({ ...c, sourceInSec: Math.min(inn, c.sourceOutSec - 0.1), timelineStartSec: Math.max(0, start) }));
      }
    }
  }

  // ── Playback wiring ──
  function togglePlay() {
    const preview = previewRef.current;
    if (!preview) return;
    if (preview.isPlaying()) {
      preview.pause();
      return;
    }
    if (playheadRef.current < previewStart || playheadRef.current >= previewEnd) preview.seek(previewStart);
    preview.play();
  }
  const changePreviewRange = (inSec: number, outSec: number, movedThumb: 0 | 1) => {
    setPreviewIn(inSec);
    setPreviewOut(outSec);
    previewRef.current?.seek(movedThumb === 0 ? inSec : outSec);
  };
  // Snap the preview range to the selected clip's span so play loops just it —
  // the replacement for the old clip editor's per-clip playback loop. Clicking
  // again (when the range already matches the clip) resets it to the whole
  // timeline, so a second click "plays everything" again.
  function loopSelectedClip() {
    if (!selectedClipId || !selectedTimelineClip) return;
    const start = timelineClipStart(timeline, selectedClipId);
    if (start == null) return;
    const end = start + clipDur(selectedTimelineClip);
    const alreadyLooping = Math.abs(previewStart - start) < 0.05 && Math.abs(previewEnd - end) < 0.05;
    if (alreadyLooping) {
      setPreviewIn(0);
      setPreviewOut(null);
      previewRef.current?.seek(0);
      return;
    }
    setPreviewIn(start);
    setPreviewOut(end);
    previewRef.current?.seek(start);
  }
  const onTime = (t: number) => {
    playheadRef.current = t;
    if (Math.abs(t - lastDisplay.current) >= 0.2) { lastDisplay.current = t; setDisplayTime(t); }
  };

  // ── Instructions ──
  async function generate() {
    if (!plan) return;
    setInstrLoading(true);
    setInstrError(null);
    setMarkdown(null);
    try {
      await service.updateEditPlan(plan.planId, planPayload(plan));
      const resp = await service.getInstructions(plan.planId);
      if (!resp?.markdown) throw new Error('The editor instructions could not be generated.');
      setMarkdown(resp.markdown);
    } catch (error) {
      setInstrError(error instanceof Error ? error.message : 'Failed to generate editor instructions.');
    } finally {
      setInstrLoading(false);
    }
  }
  // The hook and the closing CTA copy are the two things a brief cannot be handed to an
  // editor without — every other field either has a standing default or is genuinely
  // optional. Generating anyway produced a document saying "Hook: not set", which reads
  // like the tool failed rather than like something is still owed, so the gap is caught
  // here instead of shipping downstream.
  const briefGapLabel = briefGapSentence(briefGaps);

  function openInstructionsDialog() {
    const gaps = missingBriefRequirements(plan || {});
    if (gaps.hook || gaps.cta) {
      setBriefGaps(gaps);
      return;
    }
    setBriefGaps(null);
    setInstructionsDialogOpen(true);
    void generate();
  }

  // The video's script for hook generation, in priority order: the script saved
  // on the project, else the authored storyboard script, else the spoken
  // narration assembled from the audio tracks' transcripts (falling back to any
  // transcribed clip), each clipped to its trimmed range and joined in order.
  function buildHookScript(): string {
    if (plan?.script && plan.script.trim()) return plan.script.trim();
    if (segments && segments.length) {
      const authored = segments.map((s) => s.scriptText).filter(Boolean).join('\n').trim();
      if (authored) return authored;
    }
    const current = histCurrent();
    const textForClip = (sourceFileId: string, inSec: number, outSec: number): string => {
      const spans = timelineCaptionSpans(transcripts[sourceFileId], inSec, outSec);
      return (spans || []).map((s) => s.text).join(' ').trim();
    };
    const fromAudio: string[] = [];
    for (const track of current.audioTracks) {
      for (const c of [...track.clips].sort((a, b) => (a.timelineStartSec || 0) - (b.timelineStartSec || 0))) {
        const text = textForClip(c.sourceFileId, c.sourceInSec, c.sourceOutSec);
        if (text) fromAudio.push(text);
      }
    }
    if (fromAudio.length) return fromAudio.join('\n').trim();
    const fromVideo = videoLayout(current)
      .map((l) => textForClip(l.clip.sourceFileId, l.clip.sourceInSec, l.clip.sourceOutSec))
      .filter(Boolean);
    return fromVideo.join('\n').trim();
  }
  // Persist an edited/added script onto the project (autosaves like other edits).
  function saveProjectScript(script: string) {
    setPlan((p) => (p ? { ...p, script } : p));
  }
  // The dialog composes the final prompt (instructions with the script filled in)
  // and hands it over ready to send.
  async function runHookGeneration(prompt: string, model: string) {
    return service.generateHooks({ prompt, model });
  }
  // The team-shared default prompt, stored in the generic dashboard settings
  // store (Firebase) under the video-hook-prompt namespace. Returns null when
  // none is saved yet.
  // The standing wording behind the brief's [DEFAULT …] tokens. Shared across projects
  // and stored beside the shared hook prompt, so changing the house editing style is one
  // edit rather than one per project.
  async function loadBriefDefaults() {
    const resp = await settingsService.get(BRIEF_DEFAULTS_NAMESPACE);
    return (resp || {}) as Partial<Record<BriefDefaultKey, string>>;
  }
  async function saveBriefDefaults(values: Record<BriefDefaultKey, string>) {
    await settingsService.update(BRIEF_DEFAULTS_NAMESPACE, values);
    // The panel shows this wording, so it has to move with the save rather than waiting
    // for a reload to agree with the dialog the user just closed.
    setBriefDefaults(mergeBriefDefaults(values));
  }

  // Read once for the page. A failure falls back to the house wording rather than leaving
  // the panel blank: it is what an unedited brief would print anyway.
  useEffect(() => {
    let stopped = false;
    (async () => {
      try {
        const stored = await loadBriefDefaults();
        if (!stopped) setBriefDefaults(mergeBriefDefaults(stored));
      } catch {
        if (!stopped) setBriefDefaults(BRIEF_DEFAULTS);
      }
    })();
    return () => { stopped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadSharedHookPrompt() {
    const resp = await settingsService.get(HOOK_PROMPT_SETTINGS_NAMESPACE);
    const value = resp?.[HOOK_PROMPT_SETTINGS_KEY];
    return typeof value === 'string' && value.trim() ? value : null;
  }
  async function saveSharedHookPrompt(prompt: string) {
    await settingsService.update(HOOK_PROMPT_SETTINGS_NAMESPACE, { [HOOK_PROMPT_SETTINGS_KEY]: prompt });
  }
  async function loadSharedHeadlinePrompt() {
    const resp = await settingsService.get(HOOK_PROMPT_SETTINGS_NAMESPACE);
    const value = resp?.[HEADLINE_PROMPT_SETTINGS_KEY];
    return typeof value === 'string' && value.trim() ? value : null;
  }
  async function saveSharedHeadlinePrompt(prompt: string) {
    await settingsService.update(HOOK_PROMPT_SETTINGS_NAMESPACE, { [HEADLINE_PROMPT_SETTINGS_KEY]: prompt });
  }
  // Persist the chosen project-level product type / hook / headline / CTA
  // (autosaves like other edits). An empty product type means "not set" — send null
  // rather than an empty string the server would reject as an unknown category.
  function saveProjectHook(hook: string) { setPlan((p) => (p ? { ...p, hook } : p)); }
  function saveBriefField(field: 'hookOverlay' | 'editingDirection' | 'finalCta' | 'subtitlesStyle' | 'musicStyle', value: string) {
    setPlan((p) => (p ? { ...p, [field]: value } : p));
    if (briefGaps && (field === 'hookOverlay' || field === 'finalCta')) setBriefGaps(null);
  }

  // Applying a generated hook writes BOTH the ad-copy hook and the overlay section,
  // because the section is where the hook actually reaches the editor — and it arrives
  // with the timing line the template carries.
  //
  // Only the hook section is touched: the editing direction and the CTA screen are the
  // user's own writing and a hook idea has nothing to say about them. If they have
  // already written in the hook section, ask first — replacing it would throw away work
  // they cannot get back from here.
  function applyGeneratedHook(hookText: string) {
    const current = latestPlanRef.current?.hookOverlay || '';
    // The plan's own `hook` is whatever it last took from the generator, so it is what
    // separates "replace a generated hook with another" (no ceremony) from "overwrite a
    // hook the user typed" (ask first).
    if (!isUntouchedHookOverlay(current, latestPlanRef.current?.hook, briefDefaults ?? undefined)) {
      const ok = window.confirm(
        'Use this hook? It will replace what you have written in the Opening hook overlay section.',
      );
      if (!ok) return;
    }
    saveProjectHook(hookText);
    saveBriefField('hookOverlay', hookOverlayFor(hookText));
  }
  function saveProjectHeadline(headline: string) { setPlan((p) => (p ? { ...p, headline } : p)); }
  // No saveProjectCta: the closing copy is a line of the Final CTA screen section now, so
  // it is written through saveBriefField. The plan's own `cta` column is still read and
  // round-tripped by planPayload, so existing values survive — nothing writes it here.
  function saveProjectProductCategory(value: string) {
    setPlan((p) => (p ? { ...p, productCategory: value || null } : p));
  }
  async function copyMarkdown() {
    if (markdown == null) return;
    try { await navigator.clipboard.writeText(markdown); setCopyOk(true); setTimeout(() => setCopyOk(false), 1500); } catch { /* still visible to copy manually */ }
  }

  const saveLabel = { idle: '', saving: 'Saving…', saved: 'Saved', error: 'Save failed' }[saveState];
  const timelineHeight = RULER_HEIGHT + editorData.reduce((acc, r) => acc + (r.rowHeight || ROW_HEIGHT), 0) + TIMELINE_SCROLLBAR_GAP + TIMELINE_SCROLLBAR_HEIGHT + 4;

  // Coarser ruler at small zoom: pick the smallest 1/2/5/10/… second interval
  // that keeps major marks at least ~44px apart, so ticks aren't cramped when
  // the timeline is zoomed out to fit a long project.
  const rulerScaleSec = (() => {
    for (const s of [1, 2, 5, 10, 15, 30, 60]) {
      if (s * zoom >= 44) return s;
    }
    return 60;
  })();

  const selVideo = selectedId ? videoClipById.get(selectedId) : undefined;
  const selOverlay = selectedId ? overlayClipById.get(selectedId) : undefined;
  const selAudio = selectedId ? audioClipById.get(selectedId) : undefined;
  // The selected video-type clip — main track or B-roll; both share the video
  // clip editor UX (B-roll just has no audio toggle).
  const selVideoLike = selVideo || selOverlay;
  const selVideoSource = selVideoLike ? clipsById.get(selVideoLike.sourceFileId) : undefined;
  const selAudioSource = selAudio ? clipsById.get(selAudio.clip.sourceFileId) : undefined;
  const selectedTimelineClip = selVideoLike || selAudio?.clip;
  const selectedClipId = selectedTimelineClip?.id ?? null;
  // The loop button reads as active while the preview range is snapped to the
  // selected clip, so its second click ("play the whole timeline") is obvious.
  const isLoopingSelectedClip = (() => {
    if (!selectedClipId || !selectedTimelineClip) return false;
    const start = timelineClipStart(timeline, selectedClipId);
    if (start == null) return false;
    const end = start + clipDur(selectedTimelineClip);
    return Math.abs(previewStart - start) < 0.05 && Math.abs(previewEnd - end) < 0.05;
  })();
  // Video transcripts are opt-in per source; the selected-clip toolbar offers
  // the transcribe action for a video that has not been enabled yet.
  const selectedVideoSourceId = selVideo?.sourceFileId ?? null;
  const selectedVideoTranscriptEnabled = !!selectedVideoSourceId && enabledVideoTranscriptIds.has(selectedVideoSourceId);

  const linkedSelection = useMemo<LinkedTimelineClip | null>(() => {
    if (selVideoLike) {
      return {
        clipId: selVideoLike.id,
        sourceFileId: selVideoLike.sourceFileId,
        segmentId: selVideoLike.segmentId,
        inSec: selVideoLike.sourceInSec,
        outSec: selVideoLike.sourceOutSec,
        kind: 'video',
        note: selVideoLike.note || '',
      };
    }
    if (selAudio) {
      return {
        clipId: selAudio.clip.id,
        sourceFileId: selAudio.clip.sourceFileId,
        segmentId: selAudio.clip.segmentId,
        inSec: selAudio.clip.sourceInSec,
        outSec: selAudio.clip.sourceOutSec,
        kind: 'audio',
        note: selAudio.clip.note || '',
      };
    }
    return null;
  }, [selVideoLike, selAudio]);

  useEffect(() => {
    onSelectedClipChange?.(linkedSelection);
  }, [linkedSelection, onSelectedClipChange]);

  const primaryPreviewTime = playbackRange.startSec;
  const previewStartEntry = layout.find((entry) => primaryPreviewTime >= entry.start && primaryPreviewTime < entry.end)
    || layout[layout.length - 1];
  const previewStartSource = previewStartEntry ? clipsById.get(previewStartEntry.clip.sourceFileId) : null;
  const previewKey = `timeline:${plan?.planId || ''}:${previewStartEntry?.clip.id || 'none'}:${previewStartSource?.streamUrl || ''}`;
  const previewCanPlay = readyPreviewKey === previewKey;
  // Clip filmstrip backgrounds: shown unless the settings toggle hides them;
  // start capturing once the composed preview is ready so we don't compete with
  // the initial transcode/warmup.
  const showClipFrames = !hidePreviewBackgrounds;
  // Once the composed preview has been ready even once, keep filmstrip capture
  // enabled. Otherwise adding a clip changes previewKey, flips previewCanPlay
  // false, and newly-added clips' frames never load (they stay blank).
  const framesReady = previewCanPlay || framesReadyOnce;
  useEffect(() => {
    if (previewCanPlay && !framesReadyOnce) setFramesReadyOnce(true);
  }, [previewCanPlay, framesReadyOnce]);
  const timelinePreview = (
    <TimelinePreview
      ref={previewRef}
      timeline={timeline}
      clipsById={clipsById}
      timelineStateRef={timelineStateRef}
      playbackRange={playbackRange}
      onPlayStateChange={setPlaying}
      onTimeChange={onTime}
      onPrimaryVideoCanPlayChange={(canPlay) => setReadyPreviewKey(canPlay ? previewKey : null)}
    />
  );

  // The mute/unmute toggle that sits in a video clip's label bar. It stops
  // pointer/mouse propagation so clicking it toggles the clip's audio instead of
  // selecting the clip or starting a drag (same guard the delete button uses).
  const clipMuteToggle = (clip: TLVideoClip) => (
    <Tooltip title={clip.muteAudio ? 'Muted — click to use this clip’s own audio' : 'Using this clip’s audio — click to mute'} placement="top">
      <IconButton
        aria-label={clip.muteAudio ? `Unmute ${clip.fileName}` : `Mute ${clip.fileName}`}
        size="small"
        onPointerDown={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          updateVideoClip(clip.id, (c) => ({ ...c, muteAudio: !c.muteAudio }));
        }}
        sx={{ p: 0, width: 18, height: 18, flexShrink: 0, color: '#fff', '&:hover': { color: '#c7d2fe' } }}
      >
        {clip.muteAudio ? <VolumeOffIcon sx={{ fontSize: 13 }} /> : <VolumeUpIcon sx={{ fontSize: 13 }} />}
      </IconButton>
    </Tooltip>
  );

  const clipDeleteButton = (clipId: string, fileName: string) => (
    <Tooltip title="Remove this clip from the timeline" placement="top">
      <IconButton
        aria-label={`Delete ${fileName} from timeline`}
        size="small"
        onPointerDown={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          deleteTimelineClip(clipId);
        }}
        sx={{
          position: 'absolute',
          zIndex: 3,
          right: 3,
          top: '50%',
          width: 22,
          height: 22,
          p: 0,
          color: '#fff',
          backgroundColor: 'rgba(15, 23, 42, 0.7)',
          transform: 'translateY(-50%)',
          '&:hover': { color: '#fecaca', backgroundColor: 'rgba(127, 29, 29, 0.92)' },
        }}
      >
        <DeleteOutlineIcon sx={{ fontSize: 15 }} />
      </IconButton>
    </Tooltip>
  );

  const timelineTranscriptStrip = (
    clip: TLVideoClip | TLAudioClip,
    transcript: ClipTranscript | undefined,
    enabled = true,
  ) => {
    if (!enabled) return null;
    // A finished transcript with nothing in it — silent footage, or speech too
    // faint to survive transcription — has nothing to tell the editor, so it shows
    // nothing at all. (In-progress and failed transcripts still say so: those are
    // states worth acting on.)
    if (!captionsWorthShowing(clip, transcript)) return null;
    const spans = timelineCaptionSpans(transcript, clip.sourceInSec, clip.sourceOutSec);
    if (spans && spans.length > 0) {
      return (
        <Box sx={{ position: 'relative', height: captionStripHeight, mt: 0.25 }}>
          {spans.map((span, index) => (
            <Box key={index} sx={{ position: 'absolute', left: `${span.left}%`, width: `${span.width}%`, top: 0, bottom: 0, borderLeft: '1px solid rgba(255,255,255,0.45)', pl: 0.4, pr: 0.25, overflow: 'hidden' }}>
              <Typography sx={{ fontSize: captionFontSize, color: 'rgba(255,255,255,0.95)', lineHeight: 1.15, display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' }}>
                {span.text}{' '}
                <Box component="span" sx={{ fontSize: '0.78em', fontWeight: 500, opacity: 0.72, whiteSpace: 'nowrap' }}>
                  {span.durationLabel}
                </Box>
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    // Only an unfinished or failed transcript gets this far — a finished one with
    // no speech in it returned above rather than saying so.
    //
    // `pending` and `transcribing` both mean the work is genuinely in flight now
    // that opening a folder queues its transcripts, so both spin. They keep
    // separate wording because the distinction still matters when something is
    // wrong: `transcribing` means a worker has the clip, `pending` means it is
    // queued and waiting for one.
    const failed = transcript?.status === 'error';
    const statusText = failed
      ? 'transcription failed'
      : transcript?.status === 'transcribing'
        ? 'transcribing…'
        : 'fetching transcript…';
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        {!failed && <CircularProgress size={captionFontSize} sx={{ color: 'rgba(255,255,255,0.55)', flexShrink: 0 }} />}
        <Typography sx={{ fontSize: captionFontSize, color: 'rgba(255,255,255,0.7)', fontStyle: 'italic' }} noWrap>
          {statusText}
        </Typography>
      </Box>
    );
  };

  // Custom clip rendering inside each action box.
  const renderAction = (action: TimelineAction, row: TimelineRow) => {
    // The selected clip's untrimmed extent — an inert dashed band behind the
    // real bar (the CSS :has() rule strips the lane's solid background).
    if (action.id.startsWith('ghost:')) {
      return (
        <Box data-ghost-extent sx={{ height: '100%', border: '1.5px dashed rgba(100,116,139,0.65)', borderRadius: '3px', backgroundColor: 'rgba(148,163,184,0.16)', display: 'flex', alignItems: 'flex-end', px: 0.75, overflow: 'hidden' }}>
          <Typography sx={{ fontSize: 9 * fontScale, fontWeight: 700, color: '#64748b', letterSpacing: '0.03em', pb: 0.25 }} noWrap>
            FULL CLIP
          </Typography>
        </Box>
      );
    }
    if (row.id === 'script') {
      const seg = segById.get(action.id.slice('seg:'.length));
      return (
        <Box sx={{ px: 0.75, py: 0.5, height: '100%', overflow: 'hidden' }}>
          <Typography sx={{ fontSize: 10 * fontScale, fontWeight: 700, color: '#3730a3', lineHeight: 1.1 }} noWrap>{seg?.segmentRole || 'segment'}</Typography>
          <Typography sx={{ mt: 0.25, fontSize: 10 * fontScale, color: '#4b5563', lineHeight: 1.15, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{seg?.scriptText || ''}</Typography>
        </Box>
      );
    }
    if (row.id === 'video') {
      const c = videoClipById.get(action.id);
      const source = c ? clipsById.get(c.sourceFileId) : undefined;
      return (
        <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {c && source && showClipFrames && <ClipFilmstrip source={source} inSec={c.sourceInSec} outSec={c.sourceOutSec} pxWidth={clipDur(c) * zoom} loadEnabled={framesReady} />}
          {c && <ClipMediaBadge status={previewStatuses[c.sourceFileId]} pxWidth={clipDur(c) * zoom} fontScale={fontScale} />}
          <ClipLabelBar
            icon={c ? clipMuteToggle(c) : undefined}
            name={c?.fileName || 'clip'}
            meta={c ? `${clipDur(c).toFixed(1)}s${c.note ? ` · ${c.note}` : ''}` : ''}
            fontScale={fontScale}
          />
          {c && clipDeleteButton(c.id, c.fileName)}
        </Box>
      );
    }
    // Read-only caption lane beneath the video track: the clip's transcript on
    // its own strip (its own row), so the text is legible.
    if (row.id === 'videocaptions') {
      const c = videoClipById.get(action.id.slice('cap:'.length));
      const transcript = c ? transcripts[c.sourceFileId] : undefined;
      return (
        <Box sx={{ position: 'relative', height: '100%', px: 0.75, py: 0.25, overflow: 'hidden' }}>
          {c && timelineTranscriptStrip(c, transcript)}
        </Box>
      );
    }
    if (row.id === 'overlay') {
      const c = overlayClipById.get(action.id);
      const source = c ? clipsById.get(c.sourceFileId) : undefined;
      return (
        <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {c && source && showClipFrames && <ClipFilmstrip source={source} inSec={c.sourceInSec} outSec={c.sourceOutSec} pxWidth={clipDur(c) * zoom} loadEnabled={framesReady} />}
          {c && <ClipMediaBadge status={previewStatuses[c.sourceFileId]} pxWidth={clipDur(c) * zoom} fontScale={fontScale} />}
          <ClipLabelBar
            tag="B-ROLL"
            name={c?.fileName || 'clip'}
            meta={c ? `${clipDur(c).toFixed(1)}s${c.note ? ` · ${c.note}` : ''}` : ''}
            fontScale={fontScale}
          />
          {c && clipDeleteButton(c.id, c.fileName)}
        </Box>
      );
    }
    const entry = audioClipById.get(action.id);
    // Transcript overlay: the clip's spoken words/sentences positioned along the
    // bar (like the script lane's hints), so you can see what is said where.
    // Grouping (per word / N words / sentence) follows the Captions setting.
    const transcript = entry ? transcripts[entry.clip.sourceFileId] : undefined;
    const dur = entry ? clipDur(entry.clip) : 0;
    return (
      <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Audio clips carry no filmstrip, so the label bar blends into the
            clip's own (teal) body instead of the dark bar the video clips use. */}
        <ClipLabelBar
          name={entry?.clip.fileName || 'audio'}
          meta={entry ? `${dur.toFixed(1)}s${entry.clip.note ? ` · ${entry.clip.note}` : ''}` : ''}
          fontScale={fontScale}
          background="transparent"
        />
        {entry && <Box sx={{ position: 'relative', px: 0.75, flex: 1 }}>{timelineTranscriptStrip(entry.clip, transcript)}</Box>}
        {entry && clipDeleteButton(entry.clip.id, entry.clip.fileName)}
      </Box>
    );
  };

  const planControls = (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
      {plan && (
        <>
          <TextField size="small" label="Project name" sx={{ minWidth: 240, flexGrow: 1 }} value={plan.name || ''} onChange={(e) => setPlan((p) => (p ? { ...p, name: e.target.value } : p))} />
          <TextField select size="small" label="Status" sx={{ width: 130 }} value={plan.status} onChange={(e) => setPlan((p) => (p ? { ...p, status: e.target.value } : p))}>
            <MenuItem value={EDIT_PLAN_STATUS.DRAFT}>Draft</MenuItem>
            <MenuItem value={EDIT_PLAN_STATUS.READY}>Ready</MenuItem>
          </TextField>
        </>
      )}
      {/* Opening a project is done from the single top-of-page picker (all
          projects, searchable). Here we only start or delete the current one. */}
      <Button variant="contained" startIcon={<AddIcon />} onClick={() => newSeededPlan()} disabled={busy}>
        New project
      </Button>
      {plan && (
        <Button color="error" variant="outlined" onClick={() => setDeletePlanDialogOpen(true)} disabled={busy}>
          Delete project
        </Button>
      )}
      {busy && <CircularProgress size={18} />}
      {plan && saveLabel && (
        <Tooltip title={saveState === 'error' ? 'Saving failed — click to retry' : 'Edits autosave ~1s after you stop'}>
          <Chip
            size="small"
            color={saveState === 'error' ? 'error' : saveState === 'saved' ? 'success' : 'default'}
            variant={saveState === 'saved' ? 'outlined' : 'filled'}
            label={saveState === 'error' ? 'Save failed — retry' : saveLabel}
            onClick={saveState === 'error' ? () => flushSave() : undefined}
          />
        </Tooltip>
      )}
    </Box>
  );

  // The stage: the composed preview (or the selected-clip editor that takes it
  // over) plus the preview-range scrubber. Rendered into the page's shared
  // stage slot when provided, inline otherwise.
  const stageSection = (
    // Full height of the stage slot, and minHeight:0 so the flex chain below can
    // actually be constrained by it. Without a definite height here the panel's
    // maxHeight:100% resolved against an auto-height parent, so it did not constrain
    // anything — the panel grew past the stage and painted over the timeline.
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {needUserToCreate && <Alert severity="warning" sx={{ mb: 1 }}>Select your name in the top-right before creating a project — every project is saved under whoever created it.</Alert>}
      {!plan && <Alert severity="info">“New project” lays out all of this folder’s clips (video + audio) to start from — then trim, reorder, and cut. Or open a saved project.</Alert>}
      {/* The stage: the composed preview (centered) with the project's product type
          and ad copy (hook + headline + CTA) in the right-side whitespace so it's
          visible at a glance without competing with the video. */}
      {plan && (
        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 2, flex: 1, minHeight: 0 }}>
          {/* The preview keeps its natural height — only the panel fills the stage, so
              stretching the row does not change how the video sits. */}
          <Box sx={{ flexGrow: 1, minWidth: 0, minHeight: 0, alignSelf: 'flex-start' }}>{timelinePreview}</Box>
          <ProjectCopyPanel
            productCategory={plan.productCategory ?? ''}
            headline={plan.headline ?? ''}
            vocabulary={clipLabelVocabulary}
            onProductCategoryChange={saveProjectProductCategory}
            onHeadlineChange={saveProjectHeadline}
            hookOverlay={plan.hookOverlay ?? ''}
            editingDirection={plan.editingDirection ?? ''}
            finalCta={plan.finalCta ?? ''}
            subtitlesStyle={plan.subtitlesStyle ?? DEFAULT_BRIEF_CHOICE}
            musicStyle={plan.musicStyle ?? DEFAULT_BRIEF_CHOICE}
            onBriefFieldChange={saveBriefField}
            onEditDefaults={() => setBriefDefaultsOpen(true)}
            briefDefaults={briefDefaults}
            missingRequired={briefGaps}
          />
        </Box>
      )}
    </Box>
  );

  // The dock — transport + preview range + the multi-track timeline. Rendered in
  // the page's timeline slot normally, but portaled into the clip editor modal
  // (via timelineContainer) while it's open, so all tracks are editable there.
  const dockSection = (
      <Box>
        {/* The transport, the per-clip alerts and the project dialogs all act on a
            project, so they only appear once there is one. The timeline canvas
            itself (below) is NOT gated: an empty timeline is how the workspace
            reads as ready to edit, and a folder with no clips — or one whose
            project hasn't been started yet — used to render nothing at all here,
            which looked like the page had failed to load. */}
        {plan && (
          <>
          {/* Transport / tools */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', my: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="small"
              onClick={openInstructionsDialog}
              disabled={instrLoading}
              startIcon={instrLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              Generate
            </Button>
            {/* Anchored top-centre so it lands beside the brief panel it is pointing at,
                rather than at the bottom of the page away from the red boxes. */}
            <Snackbar
              open={!!briefGaps}
              autoHideDuration={6000}
              // Not on clickaway: the very click that opens this one counts as a
              // clickaway, so honouring that reason dismissed the warning the instant it
              // appeared. It closes on its timer, on its X, or when the user fills a field.
              onClose={(_e, reason) => { if (reason !== 'clickaway') setBriefGaps(null); }}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <Alert severity="warning" onClose={() => setBriefGaps(null)} sx={{ maxWidth: 460 }}>
                <strong>{briefGapLabel}</strong> before generating — the editor needs the
                words that go on screen at the start and at the end.
              </Alert>
            </Snackbar>
            <Tooltip title="Draft ad hooks for the opening from the video's script (or open a custom GPT)">
              <Button
                variant="outlined"
                size="small"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => setHookDialogOpen(true)}
              >
                Hook ideas
              </Button>
            </Tooltip>
            <Box sx={{ width: '1px', alignSelf: 'stretch', backgroundColor: 'divider', mx: 0.5 }} />
            <Tooltip title={`Undo (${UNDO_HINT})`}><span>
              <IconButton size="small" onClick={doUndo} disabled={!canUndo}><UndoIcon fontSize="small" /></IconButton>
            </span></Tooltip>
            <Tooltip title={`Redo (${REDO_HINT})`}><span>
              <IconButton size="small" onClick={doRedo} disabled={!canRedo}><RedoIcon fontSize="small" /></IconButton>
            </span></Tooltip>
            <Box sx={{ width: '1px', alignSelf: 'stretch', backgroundColor: 'divider', mx: 0.5 }} />
            <IconButton color="primary" onClick={togglePlay} size="small" aria-label={playing ? 'Pause' : 'Play'}>{playing ? <PauseIcon /> : <PlayArrowIcon />}</IconButton>
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 96 }}>
              {formatDuration(displayTime)} / {formatDuration(totalDuration)}
            </Typography>
            <Tooltip title="Split the clip under the playhead"><span>
              <Button size="small" disabled={splitting} startIcon={splitting ? <CircularProgress size={14} /> : <ContentCutIcon fontSize="small" />} onClick={splitAtPlayhead}>Split</Button>
            </span></Tooltip>
            {hasAudioTrack
              ? (
                <Button size="small" color="error" startIcon={<LibraryMusicIcon fontSize="small" />} onClick={removeAudioTrack}>
                  Remove audio track
                </Button>
              )
              : (
                <Button size="small" startIcon={<LibraryMusicIcon fontSize="small" />} onClick={addAudioTrack}>
                  Add audio track
                </Button>
              )}
            <Button size="small" startIcon={<NoteAddOutlinedIcon fontSize="small" />} onClick={openNotesDialog}>Add notes</Button>
            {segments && segments.length > 0 && (
              <Button
                size="small"
                startIcon={showScriptLane ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                onClick={() => setShowScriptLane((v) => !v)}
              >
                {showScriptLane ? 'Hide script' : 'Show script'}
              </Button>
            )}
            {/* Clip-scoped actions (only while a clip is selected). Note editing
                moved into the clip-editor modal; muting is on the clip's bar. */}
            {selectedTimelineClip && (
              <Tooltip title={isLoopingSelectedClip ? 'Looping just this clip — click to play the whole timeline again' : 'Loop playback over just this clip'}><span>
                <Button size="small" variant={isLoopingSelectedClip ? 'contained' : 'text'} onClick={loopSelectedClip}>
                  {isLoopingSelectedClip ? 'Play all' : 'Loop clip'}
                </Button>
              </span></Tooltip>
            )}
            {selVideo && !selectedVideoTranscriptEnabled && (
              <Tooltip title="Transcribe this clip's speech and show captions on its timeline bar"><span>
                <Button size="small" onClick={() => void startVideoTranscript(selVideo.sourceFileId)}>Transcribe</Button>
              </span></Tooltip>
            )}
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
              <TimelineZoomControls zoom={zoom} onChange={setZoom} label="Timeline zoom" controlName="timeline" />
            </Box>
          </Box>
          {splitError && <Alert severity="error" sx={{ mb: 1.5 }}>{splitError}</Alert>}
          {failedTranscripts.map((failure) => (
            <Alert
              key={failure.fileId}
              severity="error"
              sx={{ mb: 1.5 }}
              action={(
                <Button
                  color="inherit"
                  size="small"
                  disabled={retryingTranscriptIds.has(failure.fileId)}
                  onClick={() => retryTranscript(failure.fileId)}
                  startIcon={retryingTranscriptIds.has(failure.fileId) ? <CircularProgress size={13} color="inherit" /> : <RefreshIcon fontSize="small" />}
                >
                  Re-transcribe
                </Button>
              )}
            >
              <strong>{failure.fileName}</strong>: {failure.error}
            </Alert>
          ))}
          {selVideoLike && !selVideoSource && (
            <Alert severity="error" sx={{ mb: 1.5 }}>This clip’s source is no longer in the loaded folder. Refresh the clips before editing it.</Alert>
          )}
          {selAudio && !selAudioSource && (
            <Alert severity="error" sx={{ mb: 1.5 }}>This audio clip’s source is no longer in the loaded folder. Refresh the clips before editing it.</Alert>
          )}
          </>
        )}

          {/* Timeline — the preview range at its top controls what plays
              without changing the edit; the settings sit underneath it.
              Drop a library clip onto a lane to add it there. */}
          <Paper
            ref={timelineViewportRef}
            variant="outlined"
            onDragOver={(e) => {
              const types = Array.from(e.dataTransfer.types);
              if (!types.includes(CLIP_DRAG_MIME) && !types.includes(SEGMENT_DRAG_MIME)) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'copy';
              // Audio has exactly one valid home, so the cursor's lane is irrelevant —
              // highlighting whichever video lane it happened to be over advertised a
              // drop that was never going to happen.
              setDropTarget(types.includes(AUDIO_DRAG_MIME) ? 'audio' : laneAtClientY(e.clientY));
              const paper = timelineViewportRef.current?.getBoundingClientRect();
              setDropX(paper ? e.clientX - paper.left : null);
            }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) { setDropTarget(null); setDropX(null); } }}
            onDrop={(e) => {
              // A whole Drive clip and a saved sub-clip drop the same way; only the
              // payload differs (file id vs segment id).
              const fileId = e.dataTransfer.getData(CLIP_DRAG_MIME);
              const segmentId = e.dataTransfer.getData(SEGMENT_DRAG_MIME);
              if (!fileId && !segmentId) { setDropTarget(null); setDropX(null); return; }
              e.preventDefault();
              // Same rule as the highlight above, so what lands matches what was shown.
              const lane = e.dataTransfer.types.includes(AUDIO_DRAG_MIME)
                ? 'audio'
                : laneAtClientY(e.clientY);
              const at = timelineTimeFromClientX(e.clientX);
              if (segmentId) addSegmentToTrack(segmentId, lane, at);
              else void addClipToTrack(fileId, lane, at);
              setDropTarget(null);
              setDropX(null);
            }}
            sx={{
            position: 'relative',
            overflow: 'hidden', mb: 2,
            ...(dropTarget === 'video' ? { '& .lane-video': { outline: '2px dashed #4f46e5', outlineOffset: '-2px', backgroundColor: 'rgba(79,70,229,0.10)' } } : {}),
            '& .timeline-editor': { backgroundColor: '#fff' },
            // The library ships a dark theme: each row paints a solid #191b1d
            // gradient layer (visible as a black gutter left of time-zero on our
            // white canvas). Drop the row background-image entirely.
            '& .timeline-editor-edit-row': { backgroundImage: 'none' },
            // Ruler tick marks are white-on-white without this.
            '& .timeline-editor-time-unit': { borderRight: '1px solid rgba(0,0,0,0.15)' },
            // The library hides this scrollbar until hover and makes it only 4px
            // tall. Keep a full-size control visible so it is easy to grab.
            '& .timeline-editor-edit-area .ReactVirtualized__Grid': {
              overflowX: 'scroll !important',
              overflowY: 'hidden !important',
              boxSizing: 'border-box',
              paddingBottom: `${TIMELINE_SCROLLBAR_GAP}px`,
              scrollbarWidth: 'auto',
              scrollbarColor: '#64748b #e2e8f0',
            },
            '& .timeline-editor-edit-area .ReactVirtualized__Grid::-webkit-scrollbar': {
              display: 'block',
              height: `${TIMELINE_SCROLLBAR_HEIGHT}px !important`,
            },
            '& .timeline-editor-edit-area .ReactVirtualized__Grid::-webkit-scrollbar-track': {
              backgroundColor: '#e2e8f0 !important',
              borderRadius: '8px',
            },
            '& .timeline-editor-edit-area .ReactVirtualized__Grid::-webkit-scrollbar-thumb': {
              backgroundColor: '#64748b !important',
              border: '3px solid #e2e8f0',
              borderRadius: '8px',
            },
            '& .timeline-editor-edit-area .ReactVirtualized__Grid::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#475569 !important',
            },
            '& .lane-script .timeline-editor-action': { backgroundColor: '#e0e7ff', borderRadius: '3px' },
            // The library has no row-label gutter; pin a "B-roll" tag near
            // time-zero so the empty lane reads as the cutaway track.
            // NOTE: keep the B-roll drop highlight here, merged into this same
            // selector — a separate '& .lane-overlay' key elsewhere in this sx
            // object would override it (last duplicate key wins), which is why
            // the overlay lane previously never got its drop border.
            '& .lane-overlay': {
              position: 'relative',
              ...(dropTarget === 'overlay' ? { outline: '2px dashed #7c3aed', outlineOffset: '-2px', backgroundColor: 'rgba(124,58,237,0.12)' } : {}),
            },
            '& .lane-overlay::before': {
              content: '"B-roll"', position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)',
              fontSize: 10, fontWeight: 700, color: '#94a3b8', pointerEvents: 'none',
            },
            '& .lane-overlay .timeline-editor-action': { backgroundColor: '#7c3aed', borderRadius: '3px', zIndex: 1 },
            '& .lane-video .timeline-editor-action': { backgroundColor: '#4f46e5', borderRadius: '3px' },
            // Caption lane beneath the video track: a calm dark strip so the white
            // transcript text reads cleanly (a read-only mirror of the video clips).
            '& .lane-videocaptions': { position: 'relative' },
            '& .lane-videocaptions::before': {
              content: '"Captions"', position: 'absolute', left: 6, top: 2,
              fontSize: 9, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.04em', pointerEvents: 'none', zIndex: 0,
            },
            '& .lane-videocaptions .timeline-editor-action': { backgroundColor: '#334155', borderRadius: '3px', cursor: 'default' },
            // The audio lane's own drop highlight, in the lane's cyan. A separate key
            // from the action rule below (different selector), so both apply.
            ...(dropTarget === 'audio' && hasAudioTrack
              ? { '& .lane-audio': { outline: '2px dashed #0891b2', outlineOffset: '-2px', backgroundColor: 'rgba(8,145,178,0.12)' } }
              : {}),
            // No audio track yet: there is no lane to point at, so the canvas takes the
            // highlight. Better than inventing a placeholder row, which changed the dock's
            // height for the duration of the drag.
            ...(dropTarget === 'audio' && !hasAudioTrack
              ? { outline: '2px dashed #0891b2', outlineOffset: '-2px', backgroundColor: 'rgba(8,145,178,0.08)' }
              : {}),
            '& .lane-audio .timeline-editor-action': { backgroundColor: '#0891b2', borderRadius: '3px' },
            '& .timeline-editor-action.timeline-editor-action-selected': { outline: '2px solid #f59e0b', outlineOffset: '-1px' },
            // The selected clip's untrimmed-extent ghost: an inert dashed band
            // behind the real bar (the lane rules above would otherwise paint it
            // as a solid clip).
            '& .timeline-editor-action:has([data-ghost-extent])': {
              backgroundColor: 'transparent', pointerEvents: 'none', zIndex: 0,
            },
          }}>
            {/* Drop-position indicator while dragging a library clip in. */}
            {dropTarget && dropX != null && (
              <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `${dropX}px`, width: '2px', backgroundColor: dropTarget === 'overlay' ? '#7c3aed' : dropTarget === 'audio' ? '#0891b2' : '#4f46e5', zIndex: 5, pointerEvents: 'none', boxShadow: '0 0 0 1px rgba(255,255,255,0.6)' }} />
            )}
            {/* Preview range = a plain selection bar sitting flush on top of the
                ruler (no filmstrip — the clips carry the frames now), so the two
                read as one joined element. */}
            {totalDuration > 0 && (
              // Left padding = TIMELINE_START_LEFT − the trimmer's own 8px handle
              // inset, so the bar's time-0 lines up with the ruler's time-0.
              <Box sx={{ pl: `${TIMELINE_START_LEFT - 8}px`, pr: `${TIMELINE_START_LEFT - 8}px`, pt: 0.5, pb: 0, backgroundColor: '#fff' }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.25, flexWrap: 'wrap' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>Preview range</Typography>
                  <Tooltip title="Drag to choose what plays. Playback loops inside this selection; it does not trim clips or change the saved edit." placement="top">
                    <IconButton aria-label="How the preview range works" size="small" sx={{ p: 0.25, color: 'text.secondary' }}>
                      <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Typography variant="caption" sx={{ ml: 'auto', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                    {formatDuration(previewStart)} → {formatDuration(previewEnd)} ({(previewEnd - previewStart).toFixed(1)}s)
                  </Typography>
                </Box>
                <RangeTrimmer
                  duration={totalDuration}
                  inSec={previewStart}
                  outSec={previewEnd}
                  playheadSec={displayTime}
                  onChange={changePreviewRange}
                  onSeek={(time) => previewRef.current?.seek(time)}
                  trackHeight={18}
                  videoFrames={[]}
                  loadVideoFrames={false}
                />
              </Box>
            )}
            <Timeline
              ref={timelineStateRef}
              editorData={editorData}
              effects={EFFECTS}
              scale={rulerScaleSec}
              scaleWidth={zoom * rulerScaleSec}
              startLeft={TIMELINE_START_LEFT}
              rowHeight={ROW_HEIGHT}
              autoScroll
              style={{ width: '100%', height: timelineHeight }}
              getActionRender={renderAction}
              getScaleRender={(scale: number) => <span style={{ fontSize: 10 * fontScale, color: '#94a3b8' }}>{scale}s</span>}
              onClickAction={(e, { action, row, time }) => {
                // A click on an action bubbles to onClickRow too; stop it so
                // selecting a clip doesn't also seek. The read-only script marker
                // lane just seeks (its markers select nothing useful), and the
                // ghost extent band behaves like empty lane space.
                e.stopPropagation();
                if (row.id === 'script' || action.id.startsWith('ghost:')) { previewRef.current?.seek(time); return; }
                previewRef.current?.pause();
                // Single click selects (toggles); a double click on the same clip
                // opens the clip editor modal.
                const now = performance.now();
                const last = lastActionClick.current;
                const isDouble = !!last && last.id === action.id && (now - last.t) < 350;
                lastActionClick.current = { id: action.id, t: now };
                if (isDouble) {
                  setSelectedId(action.id);
                  onRequestClipEdit?.();
                } else {
                  setSelectedId((current) => toggleTimelineClipSelection(current, action.id));
                }
              }}
              onClickRow={(_e, { time }) => {
                // Empty space in any lane (no clip under the click, since a clip
                // stops propagation above) seeks the playhead there.
                previewRef.current?.seek(time);
              }}
              onClickTimeArea={(time) => { previewRef.current?.seek(time); return true; }}
              onCursorDrag={(time) => previewRef.current?.seek(time)}
              onActionMoveEnd={onActionMoveEnd}
              onActionResizeEnd={onActionResizeEnd}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', px: 1.25, py: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <ScrubberSettingsButton
                showBackgroundImages={!hidePreviewBackgrounds}
                onShowBackgroundImagesChange={(show) => setHidePreviewBackgrounds(!show)}
              />
            </Box>
          </Paper>

        {plan && (
          <>
          <Dialog open={deletePlanDialogOpen} onClose={() => setDeletePlanDialogOpen(false)}>
            <DialogTitle>Delete project?</DialogTitle>
            <DialogContent>
              <Typography variant="body2">
                Delete “{plan.name || 'Untitled project'}” and its saved clips and timeline? Source files and other projects will not be affected.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeletePlanDialogOpen(false)}>Cancel</Button>
              <Button color="error" variant="contained" onClick={removePlan}>Delete</Button>
            </DialogActions>
          </Dialog>

          <BriefDefaultsDialog
            open={briefDefaultsOpen}
            onClose={() => setBriefDefaultsOpen(false)}
            load={loadBriefDefaults}
            onSave={saveBriefDefaults}
          />

          <HookIdeasDialog
            open={hookDialogOpen}
            onClose={() => setHookDialogOpen(false)}
            initialScript={hookDialogOpen ? buildHookScript() : ''}
            onGenerate={runHookGeneration}
            onSaveScript={saveProjectScript}
            onUseHook={applyGeneratedHook}
            onUseHeadline={saveProjectHeadline}
            loadSharedHookPrompt={loadSharedHookPrompt}
            onSaveSharedHookPrompt={saveSharedHookPrompt}
            loadSharedHeadlinePrompt={loadSharedHeadlinePrompt}
            onSaveSharedHeadlinePrompt={saveSharedHeadlinePrompt}
          />

          <Dialog
            open={instructionsDialogOpen}
            onClose={() => setInstructionsDialogOpen(false)}
            maxWidth={false}
            PaperProps={{
              sx: {
                width: '88vw',
                height: '88vh',
                maxWidth: '88vw',
                maxHeight: '88vh',
              },
            }}
          >
            <DialogTitle>Editor instructions</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {instrLoading && (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2">Generating editor instructions…</Typography>
                </Box>
              )}
              {instrError && <Alert severity="error">{instrError}</Alert>}
              {!instrLoading && markdown != null && (
                <Box
                  component="pre"
                  aria-label="Generated editor instructions"
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    m: 0,
                    p: 2,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                    color: 'text.primary',
                    fontFamily: 'monospace',
                    fontSize: 12 * fontScale,
                    lineHeight: 1.6,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {markdown}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              {instrError && <Button onClick={() => void generate()} disabled={instrLoading}>Try again</Button>}
              {markdown != null && <Button onClick={copyMarkdown}>{copyOk ? 'Copied!' : 'Copy'}</Button>}
              {markdown != null && (
                <Button onClick={() => generateInstructionsPdf(markdown, `${(plan.name || 'edit-instructions').replace(/[^\w-]+/g, '-')}.pdf`)}>Download PDF</Button>
              )}
              <Button variant="contained" onClick={() => setInstructionsDialogOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={notesDialogOpen}
            onClose={() => setNotesDialogOpen(false)}
            maxWidth={false}
            PaperProps={{
              sx: {
                width: '50vw',
                height: '50vh',
                maxWidth: '50vw',
                maxHeight: '50vh',
              },
            }}
          >
            <DialogTitle>Overall notes</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <TextField
                autoFocus
                fullWidth
                multiline
                label="Notes"
                value={notesDraft}
                onChange={(event) => setNotesDraft(event.target.value)}
                sx={{ flex: 1, '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' }, '& textarea': { height: '100% !important', overflow: 'auto !important' } }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setNotesDialogOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={saveNotes}>Save notes</Button>
            </DialogActions>
          </Dialog>
          </>
        )}
      </Box>
  );

  return (
    <>
      {planControlsContainer && createPortal(planControls, planControlsContainer)}
      {stageContainer ? createPortal(stageSection, stageContainer) : stageSection}
      {timelineContainer ? createPortal(dockSection, timelineContainer) : dockSection}
    </>
  );
});

export default TimelineEditor;
